import SideBar from "./SideBar.js"
import ChatLayout from "./ChatLayout.js?v=45.0.25"
import {
  escapePlaintextHtml,
  sanitizeRichHtml,
} from "./chatFrontendGuards.js?v=1.0.2"
import {
  CHAT_DOCUMENT_CATALOG_LIMIT,
  CHAT_DOCUMENT_LAZY_THRESHOLD,
  LAZY_DOCUMENT_CONTEXT_REPRESENTATION,
  isLazyCatalogDocument,
  mergeMonotonicDocumentProvenance,
  mergeMonotonicDocumentState,
  privateDocumentScopeFromLocator,
} from "./documentReinjection.js?v=1.1.1"

const CHAT_ARTIFACT_ENVELOPE_VERSION = '2';

// These opaque bindings are assigned by the server when a source is resolved.
// Keep them intact through client-side cache/sync serialization; the server
// remains the authority that validates them before a document is read.
const preserveSourceBinding = (entry) => {
  if (!entry || typeof entry !== 'object') return {};
  const binding = {};
  [
    'source_id',
    'source_revision_hash',
    'source_scope_kind',
    'source_scope_id',
    'source_kind',
  ].forEach((field) => {
    if (typeof entry[field] === 'string' && entry[field].trim()) {
      binding[field] = entry[field].trim();
    }
  });
  return binding;
};

export default {
  data() {
    return {
      allSessions: {},
      activeSession: null,
      // Pagination/search state for server-backed chats
      chatsPage: 1,
      chatsPageSize: 20,
      chatsHasMore: false,
      chatsQuery: '',
      chatsTotal: 0,
      chatsLoading: false,
      chatsUsingLocalFallback: false,
      chatsLoadErrorMessage: '',
      syncEnabledFlag: typeof window !== 'undefined' ? window.sync_enabled === true : false,
      exportingSessionKey: null,
      exportProgressMessage: '',
      chatDemoRecordingQueueHandler: null,
      demoRecordingDocumentOpenHandler: null,
      demoRecordingChoiceVisible: false,
      demoRecordingChoiceResolver: null,
      demoRecordingChoicePreviousFocus: null,
      demoRecordingChoiceProfile: 'desktop'
    }
  },

  components: {
    SideBar,
    ChatLayout
  },
  created() {
    $(document).on('create_chat', (event, sessionKey, path, page_title, is_user_document, is_law) => {
      this.sessionSelected(sessionKey, path, page_title, is_user_document, is_law);
    });
    $(document).on('close-audio-call-modal', () => {
      if (document.getElementById('audioCallModal')?.classList.contains('chat-window-docked')) return;
      if (window.audioCallHandler && typeof window.audioCallHandler.closeModal === 'function') {
        window.audioCallHandler.closeModal();
      } else {
        console.warn('[App.js] audioCallHandler.closeModal() not available to handle close-audio-call-modal event.');
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('sync-status-updated', this.handleSyncStatusUpdate);
    document.removeEventListener('keydown', this.handleDemoRecordingShortcut, true);
    if (window.queueLexiusChatDemoRecordingForActiveChat === this.chatDemoRecordingQueueHandler) {
      delete window.queueLexiusChatDemoRecordingForActiveChat;
    }
    if (window.__lexiusOpenDemoChatFromDocument === this.demoRecordingDocumentOpenHandler) {
      delete window.__lexiusOpenDemoChatFromDocument;
    }
    this.finishDemoRecordingChoice(null, { restoreFocus: false });
  },
  methods: {
    getDemoRecordingAdminEmail() {
      return String(window.demo_recording_admin_email || 'admin@lexius.io').trim().toLowerCase();
    },

    currentUserIsRecordingAdmin() {
      return String(window.user_email || '').trim().toLowerCase() === this.getDemoRecordingAdminEmail();
    },

    normalizeDemoRecordingProfile(recordingProfile) {
      return recordingProfile === 'mobile' ? 'mobile' : 'desktop';
    },

    demoRecordingProfileLabel(recordingProfile) {
      return this.normalizeDemoRecordingProfile(recordingProfile) === 'mobile'
        ? 'Móvil · vertical (1080×1920)'
        : 'Escritorio · horizontal (1920×1080)';
    },

    getActiveChatUuidForRecording() {
      const layoutSession = this.$refs.chatLayout && this.$refs.chatLayout.currentSession;
      const activeUuid = layoutSession && layoutSession.uuid ? layoutSession.uuid : window.chat_uuid;
      return activeUuid ? String(activeUuid).trim() : '';
    },

    isChatModalVisibleForRecording() {
      const modalEl = document.getElementById('chatModal');
      if (!modalEl || !modalEl.classList.contains('show')) return false;
      const styles = window.getComputedStyle ? window.getComputedStyle(modalEl) : null;
      return !styles || (styles.display !== 'none' && styles.visibility !== 'hidden');
    },

    activeChatHasDocumentReviewArtifact() {
      const layout = this.$refs.chatLayout;
      const session = layout && layout.currentSession;
      const receipts = session && Array.isArray(session.tool_receipts)
        ? session.tool_receipts
        : [];
      return receipts.some(receipt => (
        receipt
        && receipt.tool_name === 'revisar_documento'
        && receipt.result_ref
        && receipt.result_ref.type === 'review'
        && !!String(receipt.result_ref.id || '').trim()
      ));
    },

    activeChatHasSecondQueryForRecording() {
      const session = this.$refs.chatLayout?.currentSession;
      const messages = Array.isArray(session?.data) ? session.data : [];
      return messages.filter(message => message?.sender === 'user').length > 1;
    },

    requestDemoRecordingChoice(recordingProfile = 'desktop') {
      if (this.demoRecordingChoiceResolver) return Promise.resolve(null);
      this.demoRecordingChoiceProfile = this.normalizeDemoRecordingProfile(recordingProfile);
      this.demoRecordingChoicePreviousFocus = document.activeElement;
      this.demoRecordingChoiceVisible = true;
      return new Promise(resolve => {
        this.demoRecordingChoiceResolver = resolve;
        this.$nextTick(() => {
          const primaryAction = this.$refs.demoRecordingReviewTourAction;
          if (primaryAction && typeof primaryAction.focus === 'function') {
            primaryAction.focus();
          }
        });
      });
    },

    finishDemoRecordingChoice(recordingFlow, { restoreFocus = true } = {}) {
      const resolve = this.demoRecordingChoiceResolver;
      const previousFocus = this.demoRecordingChoicePreviousFocus;
      this.demoRecordingChoiceResolver = null;
      this.demoRecordingChoicePreviousFocus = null;
      this.demoRecordingChoiceVisible = false;
      if (typeof resolve === 'function') resolve(recordingFlow);
      if (restoreFocus && previousFocus && typeof previousFocus.focus === 'function') {
        this.$nextTick(() => {
          if (document.contains(previousFocus)) previousFocus.focus();
        });
      }
    },

    trapDemoRecordingChoiceFocus(event) {
      const dialog = this.$refs.demoRecordingChoiceDialog;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll('[data-demo-recording-choice-focus]'))
        .filter(element => !element.disabled);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    async promptAndQueueDemoRecordingForActiveChat(recordingProfile = 'desktop') {
      if (!this.currentUserIsRecordingAdmin()) return;
      if (!this.isChatModalVisibleForRecording()) return;

      const chatUuid = this.getActiveChatUuidForRecording();
      if (!chatUuid) {
        alert('Abra un chat existente del Abogado Virtual antes de iniciar la grabación.');
        return;
      }

      let recordingFlow = '';
      if (this.activeChatHasDocumentReviewArtifact()) {
        recordingFlow = await this.requestDemoRecordingChoice(recordingProfile);
        if (recordingFlow === null) return;
      } else {
        const confirmed = confirm(`¿Desea iniciar una grabación automática de este chat del Abogado Virtual?\n\n${this.demoRecordingProfileLabel(recordingProfile)}`);
        if (!confirmed) return;
      }

      await this.queueDemoRecordingForActiveChat({ chatUuid, force: false, recordingFlow, recordingProfile });
    },

    async handleDemoRecordingShortcut(event) {
      if (!event || !event.ctrlKey || !event.altKey) return;
      const key = String(event.key || '').toLowerCase();
      if (key !== 'g' && key !== 'm' && key !== 'p') return;
      if (!this.currentUserIsRecordingAdmin()) return;
      if (!this.isChatModalVisibleForRecording()) return;

      event.preventDefault();
      event.stopPropagation();

      await this.promptAndQueueDemoRecordingForActiveChat(key === 'g' ? 'desktop' : 'mobile');
    },

    async queueDemoRecordingForActiveChat({ chatUuid, force = false, recordingFlow = '', recordingProfile = 'desktop' }) {
      try {
        const requestPayload = { uuid: chatUuid, force: !!force };
        if (this.normalizeDemoRecordingProfile(recordingProfile) === 'mobile') {
          requestPayload.recording_profile = 'mobile';
        }
        if (
          recordingFlow === 'chat-scroll'
          || recordingFlow === 'document-review-tour'
          || recordingFlow === 'document-review-workspace'
          || recordingFlow === 'document-review-second-query'
        ) {
          requestPayload.recording_flow = recordingFlow;
        }
        const response = await fetch('/admin/demo-recordings/chats', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestPayload)
        });
        let payload = {};
        try { payload = await response.json(); } catch (e) { payload = {}; }

        if (response.status === 409 && payload.code === 'recording_exists') {
          const previousUrl = payload.recording && payload.recording.s3_url ? `\n\nVideo actual: ${payload.recording.s3_url}` : '';
          const overwrite = confirm(`Este chat ya tiene una grabación completada para ${this.demoRecordingProfileLabel(recordingProfile)}. ¿Desea generar una nueva grabación?${previousUrl}`);
          if (overwrite) {
            await this.queueDemoRecordingForActiveChat({ chatUuid, force: true, recordingFlow, recordingProfile });
          }
          return;
        }

        if (response.status === 409 && payload.code === 'recording_in_progress') {
          alert('Este chat ya tiene una grabación en cola o en proceso. Revise el panel interno antes de reintentarlo.');
          return;
        }

        if (!response.ok) {
          throw new Error(payload.message || `recording_failed_${response.status}`);
        }

        const recordingUuid = payload.recording && payload.recording.recording_uuid ? payload.recording.recording_uuid : '';
        alert(recordingUuid
          ? `La grabación fue enviada a la cola.\nID: ${recordingUuid}`
          : 'La grabación fue enviada a la cola.');
      } catch (error) {
        console.error('Error queueing demo recording:', error);
        alert('No se pudo iniciar la grabación automática. Inténtelo de nuevo o revise el panel interno.');
      }
    },

    getDemoRecordingParams() {
      try {
        const params = new URLSearchParams(window.location.search || '');
        const isDemo = params.get('demo_recording') === '1';
        const feature = String(params.get('demo_feature') || '').trim().toLowerCase();
        const chatUuid = String(params.get('demo_chat_uuid') || '').trim();
        if (!isDemo || feature !== 'chats' || !chatUuid) return null;
        const startFromDocument = params.get('demo_chat_start_from_document') === '1';
        return { feature, chatUuid, startFromDocument };
      } catch (e) {
        return null;
      }
    },

    setDemoRecordingState(patch) {
      window.__lexiusDemoRecording = {
        feature: 'chats',
        ready: false,
        error: null,
        chatUuid: '',
        readyAt: null,
        scrollDelayMs: 5000,
        startFromDocument: false,
        ...(window.__lexiusDemoRecording || {}),
        ...(patch || {})
      };
    },

    closeHelpModalForDemoRecording() {
      return new Promise(resolve => {
        const modalEl = document.getElementById('helpModal');
        if (!modalEl || !modalEl.classList.contains('show')) {
          resolve();
          return;
        }

        let resolved = false;
        const finish = () => {
          if (resolved) return;
          resolved = true;
          modalEl.removeEventListener('hidden.bs.modal', finish);
          resolve();
        };

        modalEl.addEventListener('hidden.bs.modal', finish, { once: true });

        if (window.bootstrap && window.bootstrap.Modal) {
          const modal = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl);
          modal.hide();
        } else if (window.$ && typeof window.$(modalEl).modal === 'function') {
          window.$(modalEl).modal('hide');
        } else {
          modalEl.classList.remove('show');
          modalEl.style.display = 'none';
          modalEl.setAttribute('aria-hidden', 'true');
          modalEl.removeAttribute('aria-modal');
          finish();
        }

        window.setTimeout(finish, 500);
      });
    },

    waitForDemoCondition(predicate, errorMessage, timeoutMs = 15000, intervalMs = 100) {
      return new Promise((resolve, reject) => {
        const startedAt = Date.now();
        let lastError = null;
        const check = () => {
          try {
            const result = predicate();
            if (result) {
              resolve(result);
              return;
            }
          } catch (error) {
            lastError = error;
          }
          if (Date.now() - startedAt >= timeoutMs) {
            reject(lastError || new Error(errorMessage));
            return;
          }
          window.setTimeout(check, intervalMs);
        };
        check();
      });
    },

    waitForChatModalShownForDemoRecording() {
      return this.waitForDemoCondition(() => {
        const modalEl = document.getElementById('chatModal');
        if (!modalEl) return false;
        const styles = window.getComputedStyle ? window.getComputedStyle(modalEl) : null;
        const isVisible = modalEl.classList.contains('show')
          && (!styles || (styles.display !== 'none' && styles.visibility !== 'hidden'));
        return isVisible ? modalEl : false;
      }, 'chat_modal_not_visible', 5000, 50);
    },

    waitForDemoChatRendered(chatUuid) {
      return this.waitForDemoCondition(() => {
        const modalEl = document.getElementById('chatModal');
        const styles = modalEl && window.getComputedStyle ? window.getComputedStyle(modalEl) : null;
        const modalVisible = !!modalEl
          && modalEl.classList.contains('show')
          && (!styles || (styles.display !== 'none' && styles.visibility !== 'hidden'));
        if (!modalVisible) return false;

        const layout = this.$refs.chatLayout;
        const session = layout && layout.currentSession;
        const chatbox = layout && layout.$refs && layout.$refs.chatbox;
        const data = session && Array.isArray(session.data) ? session.data : [];
        const activeUuid = session && session.uuid ? String(session.uuid).trim() : '';
        const chatText = chatbox ? String(chatbox.textContent || '').trim() : '';
        const chatboxHasSpace = chatbox && chatbox.clientHeight > 100;
        const chatIsMounted = chatboxHasSpace && chatText.length > 20;

        return activeUuid === chatUuid && data.length > 0 && chatIsMounted
          ? { chatbox, messageCount: data.length }
          : false;
      }, 'chat_session_not_rendered', 15000, 100);
    },

    resetDemoChatScrollPosition() {
      try {
        const layout = this.$refs.chatLayout;
        const chatbox = layout && layout.$refs && layout.$refs.chatbox;
        if (chatbox) {
          chatbox.scrollTop = 0;
        }
      } catch (e) { }
    },

    async openDemoRecordingChat(chatUuid) {
      const targetChatUuid = String(chatUuid || '').trim();
      if (!targetChatUuid) {
        throw new Error('demo_chat_uuid_missing');
      }

      this.setDemoRecordingState({ chatUuid: targetChatUuid, ready: false, error: null, readyAt: null });
      window.sync_enabled = true;
      const showModal = await this.waitForChatModalControls();
      await this.closeHelpModalForDemoRecording();
      showModal();
      await this.waitForChatModalShownForDemoRecording();
      await this.$nextTick();
      await this.sessionSelected(targetChatUuid);
      await this.$nextTick();
      await this.waitForDemoChatRendered(targetChatUuid);
      this.ensureActiveSessionInSidebar(targetChatUuid);
      this.revealSessionInSidebar(targetChatUuid, 20);
      await this.waitForDemoSidebarSessionVisible(targetChatUuid);
      this.resetDemoChatScrollPosition();
      this.setDemoRecordingState({ ready: true, error: null, readyAt: Date.now(), scrollDelayMs: 5000 });
    },

    normalizeSidebarSessionTimestamp(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (value instanceof Date) {
        const time = value.getTime();
        return Number.isFinite(time) ? time : 0;
      }
      if (typeof value === 'string' && value.trim()) {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? 0 : parsed;
      }
      return 0;
    },

    getFirstSidebarUserMessage(session) {
      const messages = session && Array.isArray(session.data) ? session.data : [];
      return messages.find((message) => {
        if (!message || message.sender !== 'user') return false;
        return !!String(message.text || message.content || '').trim();
      }) || null;
    },

    normalizeSidebarTitleCandidate(value) {
      if (Array.isArray(value)) {
        return value.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
      }
      return String(value || '').trim();
    },

    pickSidebarSessionTitle(candidates) {
      for (const candidate of candidates) {
        const title = this.normalizeSidebarTitleCandidate(candidate);
        if (title && title.toLowerCase() !== 'chat') return title;
      }
      return 'Chat';
    },

    buildSidebarSessionFromCurrentSession(sessionKey, sourceSession = null, existingSession = {}) {
      const key = sessionKey ? String(sessionKey).trim() : '';
      if (!key) return null;

      const layoutSession = sourceSession || (this.$refs.chatLayout && this.$refs.chatLayout.currentSession);
      if (!layoutSession || typeof layoutSession !== 'object') return null;

      const sourceUuid = layoutSession.uuid ? String(layoutSession.uuid).trim() : '';
      if (sourceUuid && sourceUuid !== key) return null;

      const firstUserMessage = this.getFirstSidebarUserMessage(layoutSession);
      const firstUserText = firstUserMessage ? (firstUserMessage.text || firstUserMessage.content || '') : '';
      const title = this.pickSidebarSessionTitle([
        layoutSession.title,
        layoutSession.first_message,
        existingSession.title,
        layoutSession.page_title,
        firstUserText,
      ]);
      const canonicalTimestamp = this.normalizeSidebarSessionTimestamp(existingSession.canonical_sidebar_timestamp)
        || this.normalizeSidebarSessionTimestamp(layoutSession.canonical_sidebar_timestamp);
      const createdAt = canonicalTimestamp
        || this.normalizeSidebarSessionTimestamp(existingSession.created_at)
        || this.normalizeSidebarSessionTimestamp(layoutSession.created_at)
        || this.normalizeSidebarSessionTimestamp(layoutSession.updated_at)
        || this.normalizeSidebarSessionTimestamp(firstUserMessage && firstUserMessage.timestamp);
      const updatedAt = canonicalTimestamp
        || this.normalizeSidebarSessionTimestamp(layoutSession.updated_at)
        || this.normalizeSidebarSessionTimestamp(existingSession.updated_at)
        || createdAt;

      return {
        ...existingSession,
        uuid: key,
        title,
        data: Array.isArray(layoutSession.data) ? layoutSession.data : (existingSession.data || []),
        suggestedQuestions: Array.isArray(layoutSession.suggestedQuestions)
          ? layoutSession.suggestedQuestions
          : (existingSession.suggestedQuestions || []),
        path: layoutSession.path !== undefined ? layoutSession.path : existingSession.path,
        page_title: layoutSession.page_title !== undefined ? layoutSession.page_title : existingSession.page_title,
        is_user_document: layoutSession.is_user_document !== undefined ? layoutSession.is_user_document : existingSession.is_user_document,
        is_law: layoutSession.is_law !== undefined ? layoutSession.is_law : existingSession.is_law,
        is_multi_document: layoutSession.is_multi_document !== undefined ? !!layoutSession.is_multi_document : !!existingSession.is_multi_document,
        page_title_html: layoutSession.page_title_html !== undefined ? layoutSession.page_title_html : existingSession.page_title_html,
        is_document_chat: layoutSession.is_document_chat !== undefined ? !!layoutSession.is_document_chat : !!existingSession.is_document_chat,
        is_call_log: layoutSession.is_call_log !== undefined ? !!layoutSession.is_call_log : !!existingSession.is_call_log,
        is_pinned: Object.prototype.hasOwnProperty.call(existingSession, 'is_pinned') ? existingSession.is_pinned : !!layoutSession.is_pinned,
        is_collaborative: layoutSession.is_collaborative !== undefined ? layoutSession.is_collaborative : !!existingSession.is_collaborative,
        collaboration_role: layoutSession.collaboration_role || existingSession.collaboration_role || null,
        owner_username: layoutSession.owner_username || existingSession.owner_username || null,
        unread_count: key === String(this.activeSession || '') ? 0 : Math.max(0, Number(existingSession.unread_count || 0) || 0),
        chat_documents_state: Array.isArray(layoutSession.chat_documents_state)
          ? layoutSession.chat_documents_state
          : (existingSession.chat_documents_state || []),
        pending_document_injections: Array.isArray(layoutSession.pending_document_injections)
          ? layoutSession.pending_document_injections
          : (existingSession.pending_document_injections || []),
        canonical_sidebar_timestamp: canonicalTimestamp || null,
        created_at: createdAt,
        updated_at: updatedAt,
      };
    },

    mergeActiveSessionIntoSidebarMap(map, sessionKey = this.activeSession) {
      const key = sessionKey ? String(sessionKey).trim() : '';
      if (!key) return map;
      const baseMap = map && typeof map === 'object' ? map : {};
      const existing = baseMap[key] || {};
      const sidebarSession = this.buildSidebarSessionFromCurrentSession(key, null, existing);
      if (!sidebarSession) return baseMap;
      return { ...baseMap, [key]: sidebarSession };
    },

    ensureActiveSessionInSidebar(sessionKey = this.activeSession) {
      const key = sessionKey ? String(sessionKey).trim() : '';
      if (!key) return;
      this.allSessions = this.mergeActiveSessionIntoSidebarMap(this.allSessions, key);
    },

    revealSessionInSidebar(sessionKey = this.activeSession, attempts = 3) {
      const key = sessionKey ? String(sessionKey).trim() : '';
      if (!key) return;
      const reveal = (remainingAttempts) => {
        const sidebar = this.$refs.sideBar;
        if (sidebar && typeof sidebar.scrollSessionIntoView === 'function') {
          sidebar.scrollSessionIntoView(key);
        }
        if (remainingAttempts > 0) {
          window.setTimeout(() => {
            this.$nextTick(() => reveal(remainingAttempts - 1));
          }, 75);
        }
      };
      this.$nextTick(() => reveal(Math.max(Number(attempts) || 0, 0)));
    },

    waitForDemoSidebarSessionVisible(chatUuid) {
      const targetUuid = String(chatUuid || '').trim();
      return this.waitForDemoCondition(() => {
        if (!targetUuid) return false;
        const sidebar = this.$refs.sideBar;
        const scrollContainer = sidebar && sidebar.$refs && sidebar.$refs.sessionsScroll;
        if (!scrollContainer) return false;

        const rows = Array.from(scrollContainer.querySelectorAll('.chat-session-row'));
        const row = rows.find((candidate) => candidate.getAttribute('data-session-key') === targetUuid);
        if (!row || row.getAttribute('aria-current') !== 'true') return false;

        const containerRect = scrollContainer.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || containerRect.bottom;
        const visibleTop = Math.max(containerRect.top, 0);
        const visibleBottom = Math.min(containerRect.bottom, viewportHeight);
        const visible = rowRect.top >= visibleTop && rowRect.bottom <= visibleBottom;
        if (!visible && sidebar && typeof sidebar.scrollSessionIntoView === 'function') {
          sidebar.scrollSessionIntoView(targetUuid);
        }
        return visible ? row : false;
      }, 'chat_sidebar_session_not_visible', 5000, 50);
    },

    waitForChatModalControls(attempt = 0) {
      return new Promise((resolve, reject) => {
        const modalEl = document.getElementById('chatModal');
        if (!modalEl) {
          reject(new Error('chat_modal_missing'));
          return;
        }
        if (window.bootstrap && window.bootstrap.Modal) {
          const modal = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl);
          resolve(() => modal.show());
          return;
        }
        if (window.$ && typeof window.$(modalEl).modal === 'function') {
          resolve(() => window.$(modalEl).modal('show'));
          return;
        }
        if (attempt >= 80) {
          reject(new Error('chat_modal_controls_unavailable'));
          return;
        }
        window.setTimeout(() => {
          this.waitForChatModalControls(attempt + 1).then(resolve).catch(reject);
        }, 100);
      });
    },

    async initializeDemoRecordingMode() {
      const params = this.getDemoRecordingParams();
      if (!params) return;

      this.setDemoRecordingState({
        chatUuid: params.chatUuid,
        ready: false,
        error: null,
        startFromDocument: !!params.startFromDocument,
      });
      try {
        window.sync_enabled = true;
        if (params.startFromDocument) {
          await this.closeHelpModalForDemoRecording();
          this.demoRecordingDocumentOpenHandler = async (chatUuid = params.chatUuid) => {
            await this.openDemoRecordingChat(chatUuid);
            return window.__lexiusDemoRecording;
          };
          window.__lexiusOpenDemoChatFromDocument = this.demoRecordingDocumentOpenHandler;
          return;
        }
        await this.openDemoRecordingChat(params.chatUuid);
      } catch (error) {
        console.error('Demo recording mode failed:', error);
        this.setDemoRecordingState({
          ready: false,
          error: error && error.message ? error.message : String(error)
        });
      }
    },

    handleSyncStatusUpdate() {
      this.refreshSyncEnabledFlag();
      this.updateSessions();
    },
    retainCallTranscript(session) {
      if (!session?.uuid || !session.is_call_log || !Array.isArray(session.data) || !session.data.length) return;
      this.allSessions = { ...this.allSessions, [session.uuid]: session };
    },
    getRetainedCallTranscripts(pendingOnly = false) {
      return Object.fromEntries(Object.entries(this.allSessions || {}).filter(([, session]) => (
        session?.is_call_log && Array.isArray(session.data) && session.data.length > 0
        && (!pendingOnly || session.call_transcript_pending_save === true)
      )));
    },
    updateSessions({ page = 1, append = false, q = null } = {}) {
      // If sync is enabled, use server with pagination + search; fallback to localStorage otherwise
      if (window.sync_enabled === true) {
        this.chatsLoading = true;
        const query = q !== null ? q : this.chatsQuery;
        const url = new URL('/chats/list', window.location.origin);
        url.searchParams.set('page', String(page));
        url.searchParams.set('page_size', String(this.chatsPageSize));
        if (query && query.trim()) url.searchParams.set('q', query.trim());

        fetch(url.toString())
          .then(r => {
            if (!r.ok) throw new Error('chat_list_unavailable');
            return r.json();
          })
          .then(data => {
            const map = append ? { ...this.allSessions } : this.getRetainedCallTranscripts(true);
            (data.sessions || []).forEach(s => {
              const uuid = s.uuid;
              const previousSession = map[uuid]
                || this.allSessions[uuid]
                || this.getStoredSession(uuid)
                || {};
              map[uuid] = map[uuid] || {
                ...previousSession,
                uuid,
                data: Array.isArray(previousSession.data) ? previousSession.data : [],
                suggestedQuestions: Array.isArray(previousSession.suggestedQuestions)
                  ? previousSession.suggestedQuestions
                  : [],
                chat_documents_state: Array.isArray(previousSession.chat_documents_state)
                  ? previousSession.chat_documents_state
                  : [],
                pending_document_injections: Array.isArray(previousSession.pending_document_injections)
                  ? previousSession.pending_document_injections
                  : [],
              };
              map[uuid].title = s.first_message || s.page_title || 'Chat';
              map[uuid].path = s.path;
              map[uuid].page_title = s.page_title;
              // Carry document flags for proper sidebar icons
              try { map[uuid].is_user_document = !!s.is_user_document; } catch (e) { map[uuid].is_user_document = false; }
              try { map[uuid].is_document_chat = !!s.is_document_chat; } catch (e) { /* optional */ }
              try { map[uuid].is_pinned = (s.is_pinned === true || s.is_pinned === 1 || s.is_pinned === '1'); } catch (e) { map[uuid].is_pinned = false; }
              try { map[uuid].is_collaborative = !!s.is_collaborative; } catch (e) { map[uuid].is_collaborative = false; }
              try { map[uuid].collaboration_role = s.collaboration_role || map[uuid].collaboration_role || null; } catch (e) { }
              try { map[uuid].owner_username = s.username || map[uuid].owner_username || null; } catch (e) { }
              try {
                const unreadCount = Math.max(0, Number(s.unread_count || 0) || 0);
                map[uuid].unread_count = String(uuid) === String(this.activeSession || '') ? 0 : unreadCount;
              } catch (e) {
                map[uuid].unread_count = 0;
              }
              // Detect voice-call sessions from model column
              try { map[uuid].is_call_log = String(s.model || '').toLowerCase() === 'voice-call'; } catch (e) { /* optional */ }
              if (map[uuid].is_call_log) map[uuid].call_transcript_pending_save = false;
              try {
                const p = typeof s.path === 'string' ? s.path.trim() : '';
                map[uuid].is_multi_document = !!(p && p.startsWith('['));
              } catch (e) { map[uuid].is_multi_document = false; }
              // `/chats/list.last_updated` is the canonical sidebar clock.
              // Merely opening a chat must never replace it with a local
              // message fallback whose missing timestamp normalizes to now.
              try {
                if (s.last_updated) {
                  const ts = Date.parse(s.last_updated);
                  if (!isNaN(ts)) {
                    map[uuid].canonical_sidebar_timestamp = ts;
                    map[uuid].created_at = ts;
                    map[uuid].updated_at = ts;
                  }
                }
              } catch (e) { }
              if (Array.isArray(s.suggested_questions)) {
                map[uuid].suggestedQuestions = s.suggested_questions;
              }
              if (Array.isArray(s.chat_documents_state)) {
                map[uuid].chat_documents_state = mergeMonotonicDocumentState(
                  this.normalizeChatDocumentsStatePayload(map[uuid].chat_documents_state),
                  this.normalizeChatDocumentsStatePayload(s.chat_documents_state),
                );
              }
            });
            const visibleMap = this.mergeActiveSessionIntoSidebarMap(map);
            this.allSessions = visibleMap;
            // Update paging state
            this.chatsPage = data.page || page;
            this.chatsHasMore = !!data.has_more;
            this.chatsTotal = typeof data.total === 'number' ? Math.max(data.total, Object.keys(visibleMap).length) : Object.keys(visibleMap).length;
            if (q !== null) this.chatsQuery = q;
            this.chatsLoading = false;
            this.chatsUsingLocalFallback = false;
            this.chatsLoadErrorMessage = '';
          })
          .catch(() => {
            const parsed = { ...this.readStoredSessions(), ...this.getRetainedCallTranscripts() };
            this.allSessions = this.mergeActiveSessionIntoSidebarMap(parsed);
            this.chatsHasMore = false;
            this.chatsTotal = Object.keys(this.allSessions).length;
            this.chatsLoading = false;
            this.chatsUsingLocalFallback = true;
            this.chatsLoadErrorMessage = 'No pudimos conectar con el servidor. Mostramos los chats guardados en este dispositivo mientras vuelve la conexión.';
          });
      } else {
        const parsed = { ...this.readStoredSessions(), ...this.getRetainedCallTranscripts(true) };
        this.allSessions = this.mergeActiveSessionIntoSidebarMap(parsed);
        this.chatsHasMore = false;
        this.chatsTotal = Object.keys(this.allSessions).length;
        this.chatsLoading = false;
        this.chatsUsingLocalFallback = false;
        this.chatsLoadErrorMessage = '';
      }
    },

    // Triggered by SideBar search input
    handleSearchQueryChanged(q) {
      this.chatsPage = 1;
      this.updateSessions({ page: 1, append: false, q });
    },

    // Triggered by SideBar "Load more"
    loadMoreSessions() {
      if (!this.chatsHasMore) return;
      const next = (this.chatsPage || 1) + 1;
      this.updateSessions({ page: next, append: true });
    },

    retryFetchingServerChats() {
      if (window.sync_enabled !== true) return;
      if (this.chatsLoading) return;
      this.updateSessions({ page: 1, append: false });
    },

    handleCollaborationRemoved(sessionKey) {
      const keyValue = String(sessionKey || '').trim();
      if (!keyValue) return;
      const map = { ...this.allSessions };
      delete map[keyValue];
      this.allSessions = map;
      try {
        const storageKey = 'lexius_virtual_lawyer_chats';
        const raw = localStorage.getItem(storageKey) ?? '{}';
        const parsed = JSON.parse(raw);
        delete parsed[keyValue];
        localStorage.setItem(storageKey, JSON.stringify(parsed));
      } catch (e) { }
      if (this.activeSession === keyValue) {
        this.activeSession = null;
      }
      if (window.sync_enabled === true) {
        this.updateSessions({ page: 1, append: false });
      }
    },

    refreshSyncEnabledFlag() {
      this.syncEnabledFlag = typeof window !== 'undefined' && window.sync_enabled === true;
    },

    normalizeChatShortcutDocuments(rawDocuments) {
      let documents = rawDocuments;
      if (documents && !Array.isArray(documents) && Array.isArray(documents.documents)) {
        documents = documents.documents;
      } else if (documents && !Array.isArray(documents) && Array.isArray(documents.paths)) {
        const titles = Array.isArray(documents.page_titles) ? documents.page_titles : [];
        const userDocumentFlags = Array.isArray(documents.is_user_documents)
          ? documents.is_user_documents
          : [];
        documents = documents.paths.map((path, index) => ({
          path,
          page_title: titles[index],
          is_user_document: userDocumentFlags[index],
          is_law: documents.is_law,
        }));
      }

      if (!Array.isArray(documents)) {
        documents = documents === undefined || documents === null ? [] : [documents];
      }

      const coerceBoolean = (value, fallback) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
          const normalized = value.trim().toLowerCase();
          if (['true', '1', 'yes', 'si', 'sí'].includes(normalized)) return true;
          if (['false', '0', 'no'].includes(normalized)) return false;
        }
        if (value === 1) return true;
        if (value === 0) return false;
        return fallback;
      };

      const titleFromPath = (pathValue) => {
        try {
          const url = new URL(pathValue, window.location.origin);
          const title = url.searchParams.get('original_filename');
          if (title) return title;
        } catch (e) { }
        const pathWithoutQuery = String(pathValue || '').split('?', 1)[0];
        const fallback = pathWithoutQuery.split('/').filter(Boolean).pop() || 'Documento';
        try { return decodeURIComponent(fallback); } catch (e) { return fallback; }
      };

      return documents.map((document) => {
        const item = document && typeof document === 'object'
          ? document
          : { path: document };
        const path = String(
          item.path
          || item.href
          || item.url
          || item.file_path
          || ''
        ).trim();
        if (!path) return null;
        const pageTitle = String(
          item.page_title
          || item.pageTitle
          || item.title
          || item.original_filename
          || item.originalFilename
          || item.name
          || titleFromPath(path)
        ).trim();
        const userDocumentCandidate = item.is_user_document !== undefined
          ? item.is_user_document
          : item.isUserDocument;
        const lawCandidate = item.is_law !== undefined ? item.is_law : item.isLaw;
        return {
          path,
          page_title: pageTitle || 'Documento',
          // This callback only receives entries selected from Mis archivos.
          // Treat an omitted flag as a user-library document even when a
          // caller supplies a storage path instead of the usual endpoint URL.
          is_user_document: coerceBoolean(userDocumentCandidate, true),
          is_law: coerceBoolean(lawCandidate, false),
        };
      }).filter(Boolean);
    },

    isChatSessionTrulyEmpty(session) {
      if (!session || typeof session !== 'object') return true;
      const hasUuid = !!String(session.uuid || '').trim();
      const hasMessages = Array.isArray(session.data) && session.data.length > 0;
      const paths = Array.isArray(session.path) ? session.path : [session.path];
      const hasPath = paths.some((path) => !!String(path || '').trim());
      const hasDocumentState = Array.isArray(session.chat_documents_state)
        && session.chat_documents_state.some((entry) => entry && String(entry.path || '').trim());
      const hasPendingDocuments = Array.isArray(session.pending_document_injections)
        && session.pending_document_injections.some((entry) => entry && String(entry.path || '').trim());
      return !hasUuid && !hasMessages && !hasPath && !hasDocumentState && !hasPendingDocuments;
    },

    openUserFilesFromChatShortcut() {
      const chatLayout = this.$refs.chatLayout;
      const originSession = chatLayout?.currentSession || null;
      const isCurrentChat = () => {
        const currentSession = this.$refs.chatLayout?.currentSession || null;
        if (!originSession || !currentSession) return false;
        if (currentSession === originSession) return true;

        const originUuid = String(originSession.uuid || '').trim();
        const currentUuid = String(currentSession.uuid || '').trim();
        return !!originUuid && originUuid === currentUuid;
      };
      const originContext = { originSession, isCurrentChat };
      const attachDocuments = (documents) => (
        this.attachDocumentsFromChatShortcut(documents, originContext)
      );
      if (typeof window.openUserFilesFromChatShortcut === 'function') {
        return window.openUserFilesFromChatShortcut({ attachDocuments, isCurrentChat });
      }

      // Keep the shortcut useful if the files-library bridge has not loaded
      // yet, while preserving the regular modal behavior as a safe fallback.
      if (typeof window.openUserFilesModal === 'function') {
        return window.openUserFilesModal();
      }
      $(document).trigger('open-user-files-select');
      return undefined;
    },

    async attachDocumentsFromChatShortcut(rawDocuments, originContext = null) {
      const documents = this.normalizeChatShortcutDocuments(rawDocuments);
      if (!documents.length || !this.$refs.chatLayout) return { status: 'unavailable' };
      if (
        originContext
        && typeof originContext.isCurrentChat === 'function'
        && originContext.isCurrentChat() !== true
      ) {
        return { status: 'chat-changed' };
      }

      const paths = documents.map((document) => document.path);
      const pageTitles = documents.map((document) => document.page_title);
      const userDocumentFlags = documents.map((document) => document.is_user_document);
      const pathPayload = documents.length === 1 ? paths[0] : paths;
      const pageTitlePayload = documents.length === 1 ? pageTitles[0] : pageTitles;
      const userDocumentPayload = documents.length === 1
        ? userDocumentFlags[0]
        : userDocumentFlags;
      const lawPayload = documents.length === 1 ? documents[0].is_law : false;
      const activeChat = this.$refs.chatLayout.currentSession;

      if (this.isChatSessionTrulyEmpty(activeChat)) {
        if (documents.length > CHAT_DOCUMENT_LAZY_THRESHOLD) {
          const attached = await this.checkAndHandleDocumentAddition(
            pathPayload,
            pageTitlePayload,
            userDocumentPayload,
            lawPayload,
            {
              preconfirmed: true,
              allowEmptyChat: true,
            }
          );
          const didAttach = attached && typeof attached === 'object'
            ? attached.attached === true
            : !!attached;
          return { status: didAttach ? 'attached' : 'unavailable' };
        }
        const result = await this.$refs.chatLayout.switchSession(
          null,
          pathPayload,
          pageTitlePayload,
          userDocumentPayload,
          lawPayload
        );
        this.activeSession = null;
        return { status: !result || result.ok !== false ? 'attached' : 'unavailable' };
      }

      const attached = await this.checkAndHandleDocumentAddition(
        pathPayload,
        pageTitlePayload,
        userDocumentPayload,
        lawPayload,
        {
          preconfirmed: true,
          allowWhileReplying: true,
        }
      );
      const didAttach = attached && typeof attached === 'object'
        ? attached.attached === true
        : !!attached;
      return { status: didAttach ? 'attached' : 'unavailable' };
    },

    async checkAndHandleDocumentAddition(path, page_title, is_user_document, is_law, options = {}) {
      const additionOptions = options && typeof options === 'object' ? options : {};
      const activeChat = this.$refs.chatLayout?.currentSession;
      if (!activeChat) return false;
      if (this.$refs.chatLayout?.replying && additionOptions.allowWhileReplying !== true) return false;
      const isCallLogSession = (
        activeChat.is_call_log === true
        || activeChat.is_call_log === 1
        || activeChat.is_call_log === '1'
        || String(activeChat.is_call_log || '').toLowerCase() === 'true'
      );
      if (isCallLogSession) return false;
      const activeChatHasContext = !!activeChat.uuid
        || !!activeChat.path
        || (Array.isArray(activeChat.data) && activeChat.data.length > 0)
        || (Array.isArray(activeChat.chat_documents_state) && activeChat.chat_documents_state.length > 0)
        || (Array.isArray(activeChat.pending_document_injections) && activeChat.pending_document_injections.length > 0);
      if (!activeChatHasContext && additionOptions.allowEmptyChat !== true) return false;

      const normalizeIsUserDocValue = (value, fallbackPath) => {
        if (typeof fallbackPath === 'string' && fallbackPath.includes('/get_user_upload')) return true;
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
          const trimmed = value.trim().toLowerCase();
          if (['true', '1', 'yes', 'si', 'sí'].includes(trimmed)) return true;
          if (['false', '0', 'no', 'null', 'none', ''].includes(trimmed)) return false;
        }
        if (value === undefined || value === null) {
          if (typeof fallbackPath === 'string' && fallbackPath.includes('/get_user_upload')) return true;
          return false;
        }
        return !!value;
      };

      const toArray = (value) => {
        if (Array.isArray(value)) return value;
        if (value === undefined || value === null) return [];
        return [value];
      };

      const escapeHtml = (value) => String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

      const serializeDocumentStateForSync = (rawState) => {
        if (!Array.isArray(rawState)) return [];
        return rawState
          .filter((entry) => entry && typeof entry === 'object')
          .map((entry) => ({
            doc_id: entry.doc_id || entry.key || '',
            path: entry.path == null ? '' : String(entry.path),
            page_title: entry.page_title == null ? '' : String(entry.page_title),
            is_user_document: !!entry.is_user_document,
            ...preserveSourceBinding(entry),
            ...(isLazyCatalogDocument(entry)
              ? { context_representation: LAZY_DOCUMENT_CONTEXT_REPRESENTATION }
              : {}),
            added_during: entry.added_during === 'added_later' ? 'added_later' : 'initial',
            is_out_of_context: !!entry.is_out_of_context,
            pending_reinjection: !!entry.pending_reinjection,
            injection_message_id: entry.injection_message_id || null,
            out_of_context_at: entry.out_of_context_at || null,
            last_injected_at: entry.last_injected_at || null,
          }));
      };

      const persistDocumentMetadataIfSyncEnabled = async (expectedCheckpointVersion = null) => {
        if (window.sync_enabled !== true) return { availabilityHandled: false };
        if (!activeChat.uuid) return { availabilityHandled: false };
        try {
          const payloadItem = {
            uuid: activeChat.uuid,
            path: activeChat.path,
            page_title: activeChat.page_title,
            is_user_document: activeChat.is_user_document,
            chat_documents_state: serializeDocumentStateForSync(activeChat.chat_documents_state),
          };
          const headers = { 'Content-Type': 'application/json' };
          try {
            headers['device-uuid'] = new DeviceUUID().get();
          } catch (e) { }

          const response = await fetch('/sync/migrate/chats', {
            method: 'POST',
            headers,
            body: JSON.stringify({
              chat_uuids: [activeChat.uuid],
              items: [payloadItem],
              activate: false,
            }),
          });
          const responsePayload = await response.json().catch(() => ({}));
          const chatLayout = this.$refs.chatLayout;
          const chatUuid = String(activeChat.uuid || '').trim();
          const metadataResults = responsePayload
            && responsePayload.document_metadata_results;
          const metadataResult = (
            metadataResults
            && typeof metadataResults === 'object'
            && !Array.isArray(metadataResults)
          ) ? metadataResults[chatUuid] : null;
          let availabilityHandled = false;
          const currentSession = chatLayout && chatLayout.currentSession;
          const currentCheckpointVersion = (
            chatLayout
            && typeof chatLayout.currentDocumentAdditionCheckpointVersion === 'function'
          ) ? chatLayout.currentDocumentAdditionCheckpointVersion(activeChat) : null;
          const checkpointMatches = expectedCheckpointVersion === null
            ? currentCheckpointVersion === null
            : Number(currentCheckpointVersion) === Number(expectedCheckpointVersion);
          const responseStillMatchesSession = !!(
            chatLayout
            && currentSession === activeChat
            && String(currentSession.uuid || '').trim() === chatUuid
            && checkpointMatches
          );

          if (response.ok && responseStillMatchesSession) {
            const documentAvailability = (
              metadataResult
              && typeof metadataResult === 'object'
              && metadataResult.document_availability
            ) || (responsePayload && responsePayload.document_availability);
            if (
              documentAvailability
              && typeof chatLayout.applyDocumentAvailabilityFeedback === 'function'
            ) {
              chatLayout.applyDocumentAvailabilityFeedback(
                {
                  ...documentAvailability,
                  pruned_count: metadataResult && metadataResult.pruned_count,
                },
                activeChat
              );
              availabilityHandled = true;
            }
            if (
              metadataResult
              && typeof metadataResult === 'object'
              && typeof chatLayout.applyAuthoritativeDocumentMetadata === 'function'
            ) {
              chatLayout.applyAuthoritativeDocumentMetadata(metadataResult, activeChat);
            }
            if (typeof chatLayout.clearDocumentAdditionCheckpoint === 'function') {
              chatLayout.clearDocumentAdditionCheckpoint(
                chatUuid,
                expectedCheckpointVersion,
              );
            }
          }
          const typedErrorCode = [
            responsePayload && responsePayload.code,
            responsePayload && responsePayload.error_code,
            responsePayload && responsePayload.error,
          ].map(value => String(value || '').trim()).find(value => [
            'document_catalog_capacity_exceeded',
            'document_reader_unavailable',
            'chat_document_metadata_conflict',
          ].includes(value)) || '';
          return {
            availabilityHandled,
            typedErrorCode: response.ok || !responseStillMatchesSession
              ? ''
              : typedErrorCode,
            stale: !responseStillMatchesSession,
          };
        } catch (e) {
          // Non-blocking best effort; local state is already persisted.
          return { availabilityHandled: false };
        }
      };

      const docKey = (docPath, isUserDocument, scopeHint = '', sourceBinding = null) => (
        this.buildDocumentKey(
          docPath,
          isUserDocument,
          scopeHint,
          sourceBinding,
        )
      );

      const normalizeIncomingDocs = () => {
        const paths = toArray(path).map((item) => (item == null ? '' : String(item)));
        const titlesRaw = toArray(page_title);
        const userRaw = Array.isArray(is_user_document)
          ? is_user_document
          : Array(paths.length).fill(is_user_document);

        const docs = [];
        const seen = new Set();
        for (let i = 0; i < paths.length; i++) {
          const currentPath = (paths[i] || '').trim();
          if (!currentPath) continue;
          const currentTitle = (titlesRaw[i] == null ? '' : String(titlesRaw[i])).trim();
          const currentIsUser = normalizeIsUserDocValue(userRaw[i], currentPath);
          const key = docKey(currentPath, currentIsUser);
          if (seen.has(key)) continue;
          seen.add(key);
          docs.push({
            path: currentPath,
            page_title: currentTitle,
            is_user_document: currentIsUser,
            key,
          });
        }
        return docs;
      };

      const extractDocsFromSession = () => {
        const paths = Array.isArray(activeChat.path) ? activeChat.path : (activeChat.path ? [activeChat.path] : []);
        const titles = Array.isArray(activeChat.page_title) ? activeChat.page_title : (paths.length ? [activeChat.page_title] : []);
        let isUsers = [];
        if (Array.isArray(activeChat.is_user_document)) {
          isUsers = activeChat.is_user_document;
        } else {
          isUsers = Array(paths.length).fill(activeChat.is_user_document);
        }

        const docs = [];
        const normalizedState = Array.isArray(activeChat.chat_documents_state)
          ? activeChat.chat_documents_state
          : [];
        for (let i = 0; i < paths.length; i++) {
          const currentPath = (paths[i] == null ? '' : String(paths[i])).trim();
          if (!currentPath) continue;
          const currentIsUser = normalizeIsUserDocValue(isUsers[i], currentPath);
          const alignedState = normalizedState[i];
          const stateMatchesLegacyRow = !!(
            alignedState
            && String(alignedState.path || '').trim() === currentPath
            && normalizeIsUserDocValue(
              alignedState.is_user_document,
              alignedState.path,
            ) === currentIsUser
          );
          const scopeHint = stateMatchesLegacyRow
            ? alignedState.source_scope_kind
              || alignedState.scope
              || alignedState.user_document_scope
              || ''
            : '';
          docs.push({
            path: currentPath,
            page_title: titles[i] == null ? '' : String(titles[i]),
            is_user_document: currentIsUser,
            key: docKey(
              currentPath,
              currentIsUser,
              scopeHint,
              stateMatchesLegacyRow ? alignedState : null,
            ),
          });
        }
        return docs;
      };

      const ensureDocStateArray = () => {
        if (!Array.isArray(activeChat.chat_documents_state)) {
          activeChat.chat_documents_state = [];
        }
        const state = [];
        for (const entry of activeChat.chat_documents_state) {
          if (!entry || typeof entry !== 'object') continue;
          const entryPath = (entry.path == null ? '' : String(entry.path)).trim();
          if (!entryPath) continue;
          const entryIsUser = normalizeIsUserDocValue(entry.is_user_document, entryPath);
          const key = docKey(
            entryPath,
            entryIsUser,
            entry.source_scope_kind || entry.scope || entry.user_document_scope,
            entry,
          );
          state.push({
            doc_id: entry.doc_id || key,
            path: entryPath,
            page_title: entry.page_title == null ? '' : String(entry.page_title),
            is_user_document: entryIsUser,
            ...preserveSourceBinding(entry),
            ...(isLazyCatalogDocument(entry)
              ? { context_representation: LAZY_DOCUMENT_CONTEXT_REPRESENTATION }
              : {}),
            added_during: entry.added_during === 'added_later' ? 'added_later' : 'initial',
            is_out_of_context: !!entry.is_out_of_context,
            pending_reinjection: !!entry.pending_reinjection,
            injection_message_id: entry.injection_message_id || null,
            out_of_context_at: entry.out_of_context_at || null,
            last_injected_at: entry.last_injected_at || null,
            key,
          });
        }
        activeChat.chat_documents_state = state;
        return activeChat.chat_documents_state;
      };

      const ensureStateHasSessionDocs = () => {
        const state = ensureDocStateArray();
        const sessionDocs = extractDocsFromSession();
        const map = new Map(state.map((entry) => [entry.key, entry]));
        for (const doc of sessionDocs) {
          if (map.has(doc.key)) continue;
          state.push({
            doc_id: doc.key,
            path: doc.path,
            page_title: doc.page_title || '',
            is_user_document: doc.is_user_document,
            ...(isLazyCatalogDocument(doc)
              ? { context_representation: LAZY_DOCUMENT_CONTEXT_REPRESENTATION }
              : {}),
            added_during: 'initial',
            is_out_of_context: false,
            pending_reinjection: false,
            injection_message_id: null,
            out_of_context_at: null,
            last_injected_at: null,
            key: doc.key,
          });
          map.set(doc.key, state[state.length - 1]);
        }
        return state;
      };

      const applySessionDocs = (docs) => {
        if (!docs.length) {
          activeChat.path = null;
          activeChat.page_title = null;
          activeChat.is_user_document = null;
          activeChat.is_multi_document = false;
          activeChat.page_title_html = null;
          return;
        }

        if (docs.length === 1) {
          activeChat.path = docs[0].path;
          activeChat.page_title = docs[0].page_title;
          activeChat.is_user_document = docs[0].is_user_document;
          activeChat.is_multi_document = false;
          activeChat.page_title_html = null;
          return;
        }

        activeChat.path = docs.map((doc) => doc.path);
        activeChat.page_title = docs.map((doc) => doc.page_title);
        activeChat.is_user_document = docs.map((doc) => doc.is_user_document);
        activeChat.is_multi_document = true;
        activeChat.page_title_html = "<ol style='margin-left:2%;'>" + docs.map((doc) => (
          `<li><a href='${doc.path}' class='text-secondary font-bold text-center'>${doc.page_title}</a></li>`
        )).join('') + "</ol>";
      };

      const enqueuePendingInjections = (docs) => {
        if (!Array.isArray(activeChat.pending_document_injections)) {
          activeChat.pending_document_injections = [];
        }
        const queueMap = new Map();
        activeChat.pending_document_injections.forEach((entry) => {
          if (!entry || typeof entry !== 'object') return;
          const key = docKey(entry.path, normalizeIsUserDocValue(entry.is_user_document, entry.path));
          queueMap.set(key, {
            path: entry.path,
            page_title: entry.page_title || '',
            is_user_document: normalizeIsUserDocValue(entry.is_user_document, entry.path),
            force_reinject: !!entry.force_reinject,
            catalog_binding_only: !!entry.catalog_binding_only,
          });
        });

        docs.forEach((doc) => {
          const key = doc.key;
          const existing = queueMap.get(key);
          if (existing) {
            if (doc.force_reinject) existing.force_reinject = true;
            if (doc.catalog_binding_only) existing.catalog_binding_only = true;
            return;
          }
          queueMap.set(key, {
            path: doc.path,
            page_title: doc.page_title || '',
            is_user_document: doc.is_user_document,
            force_reinject: !!doc.force_reinject,
            catalog_binding_only: !!doc.catalog_binding_only,
          });
        });

        activeChat.pending_document_injections = Array.from(queueMap.values());
      };

      const showConfirmModal = ({ messageHtml, confirmText, cancelText = 'Crear nuevo chat' }) => (
        new Promise((resolve) => {
          const modal = document.getElementById('addDocumentConfirmModal');
          const messageEl = document.getElementById('addDocumentConfirmMessage');
          const confirmBtn = document.getElementById('confirmAddDocument');
          const cancelBtn = document.getElementById('cancelAddDocument');
          const keepBtn = document.getElementById('keepContextBtn');

          if (!modal || !messageEl || !confirmBtn || !cancelBtn) {
            resolve(false);
            return;
          }

          if (keepBtn) keepBtn.style.display = 'none';
          messageEl.innerHTML = messageHtml;
          confirmBtn.textContent = confirmText;
          cancelBtn.textContent = cancelText;

          const bsModal = bootstrap.Modal.getOrCreateInstance
            ? bootstrap.Modal.getOrCreateInstance(modal)
            : new bootstrap.Modal(modal);
          const invocationToken = {};
          modal.__lexiusDocumentConfirmation = invocationToken;
          let fallbackTimer = null;
          let settled = false;
          let requestedResult = false;

          const forceHideModal = () => {
            const isStillVisible = modal.classList.contains('show') || modal.style.display === 'block';
            if (!isStillVisible) return;
            modal.classList.remove('show');
            modal.style.display = 'none';
            modal.setAttribute('aria-hidden', 'true');
            modal.removeAttribute('aria-modal');
            modal.removeAttribute('role');
            const backdrops = Array.from(document.querySelectorAll('.modal-backdrop'));
            const lastBackdrop = backdrops[backdrops.length - 1];
            if (lastBackdrop) lastBackdrop.remove();
            const hasOtherVisibleModals = Array.from(document.querySelectorAll('.modal.show'))
              .some((candidate) => candidate !== modal && !candidate.classList.contains('chat-window-docked'));
            if (hasOtherVisibleModals) {
              document.body.classList.add('modal-open');
            } else {
              document.body.classList.remove('modal-open');
              document.body.style.removeProperty('overflow');
              document.body.style.removeProperty('padding-right');
            }
          };

          const clearHandlers = () => {
            confirmBtn.removeEventListener('click', handleConfirm);
            cancelBtn.removeEventListener('click', handleCancel);
            modal.removeEventListener('hidden.bs.modal', handleHidden);
          };

          const finish = (result) => {
            if (settled) return;
            settled = true;
            if (fallbackTimer) window.clearTimeout(fallbackTimer);
            clearHandlers();
            if (modal.__lexiusDocumentConfirmation === invocationToken) {
              delete modal.__lexiusDocumentConfirmation;
            }
            resolve(result);
          };

          const requestHide = (result) => {
            requestedResult = result;
            try { bsModal.hide(); } catch (e) { }
            fallbackTimer = window.setTimeout(() => {
              if (modal.__lexiusDocumentConfirmation === invocationToken) {
                forceHideModal();
                try {
                  if (typeof bsModal.dispose === 'function') bsModal.dispose();
                } catch (e) { }
                // Forced closure skips hidden.bs.modal. Restore window stacking
                // after disposal has released this confirmation's focus trap.
                modal.dispatchEvent(new Event('lexius-modal-force-hidden', { bubbles: true }));
              }
              finish(result);
            }, 350);
          };

          const handleConfirm = () => {
            requestHide(true);
          };

          const handleCancel = (event) => {
            // This button also has Bootstrap's delegated dismiss attribute.
            // Stop that later handler from hiding a newly opened follow-up
            // confirmation after this promise resolves.
            if (event) {
              event.preventDefault();
              event.stopPropagation();
            }
            requestHide(false);
          };

          const handleHidden = () => {
            finish(requestedResult);
          };

          confirmBtn.addEventListener('click', handleConfirm);
          cancelBtn.addEventListener('click', handleCancel);
          modal.addEventListener('hidden.bs.modal', handleHidden);
          bsModal.show();
        })
      );

      const incomingDocs = normalizeIncomingDocs();
      if (!incomingDocs.length) return false;

      const hasLocalMessages = Array.isArray(activeChat.data) && activeChat.data.length > 0;
      const hasExistingServerChat = !!activeChat.uuid && window.sync_enabled === true;
      const hasMessages = hasLocalMessages || hasExistingServerChat;
      const state = ensureStateHasSessionDocs();
      const existingMap = new Map(state.map((entry) => [entry.key, entry]));
      const requestedNewCount = incomingDocs.filter((doc) => !existingMap.has(doc.key)).length;
      const projectedDocumentCount = existingMap.size + requestedNewCount;
      // This flag controls only the internal document representation. Reader-capable
      // chats can use lazy-v1 even below the threshold, so it must not change the
      // user's existing choice between this chat and a new one.
      const usesLazyCatalog = projectedDocumentCount > CHAT_DOCUMENT_LAZY_THRESHOLD
        || state.some(isLazyCatalogDocument);
      const collaborationValue = typeof activeChat.is_collaborative === 'string'
        ? activeChat.is_collaborative.trim().toLowerCase()
        : activeChat.is_collaborative;
      const explicitlyCollaborative = collaborationValue === true
        || collaborationValue === 1
        || collaborationValue === '1'
        || collaborationValue === 'true';
      const explicitlyNonCollaborative = collaborationValue === false
        || collaborationValue === 0
        || collaborationValue === '0'
        || collaborationValue === 'false';
      const isCollaborativeActiveChat = explicitlyCollaborative
        || (!explicitlyNonCollaborative && !!activeChat.collaboration_role);

      const rejectCatalogCapacity = () => {
        const capacityMessage = `Este chat admite hasta ${CHAT_DOCUMENT_CATALOG_LIMIT} documentos. No se agregó ningún documento nuevo.`;
        if (typeof this.$refs.chatLayout.presentDocumentAvailabilityNotice === 'function') {
          this.$refs.chatLayout.presentDocumentAvailabilityNotice({
            tone: 'error',
            title: 'No se agregaron los documentos',
            detail: capacityMessage,
          }, activeChat);
        } else {
          window.alert(capacityMessage);
        }
        return { handled: true, attached: false };
      };
      const exceedsCurrentChatCapacity = projectedDocumentCount > CHAT_DOCUMENT_CATALOG_LIMIT;
      if (
        incomingDocs.length > CHAT_DOCUMENT_CATALOG_LIMIT
        || (exceedsCurrentChatCapacity && additionOptions.preconfirmed === true)
      ) {
        return rejectCatalogCapacity();
      }

      const docsToAppendToSession = [];
      const docsToInjectNextMessage = [];
      const alreadyInContextDocs = [];
      const warnedDocs = [];

      incomingDocs.forEach((doc) => {
        const existing = existingMap.get(doc.key);
        if (!existing) {
          docsToAppendToSession.push(doc);
          if (hasMessages) {
            docsToInjectNextMessage.push(doc);
          }
          return;
        }

        if (
          !usesLazyCatalog
          && existing.added_during === 'added_later'
          && existing.is_out_of_context
        ) {
          warnedDocs.push(doc);
          docsToInjectNextMessage.push({ ...doc, force_reinject: true });
          return;
        }

        alreadyInContextDocs.push(doc);
      });

      if (!docsToAppendToSession.length && !docsToInjectNextMessage.length) {
        if (additionOptions.preconfirmed === true) {
          return true;
        }
        const existingTitles = state.map((entry) => escapeHtml(entry.page_title || entry.path));
        const messageHtml = `
          <p>${incomingDocs.length === 1 ? 'El documento' : 'Los documentos'} que intenta agregar ya ${incomingDocs.length === 1 ? 'forma' : 'forman'} parte del contexto del chat actual.</p>
          <div class="mt-3">
            <p><strong>Documentos en el chat actual:</strong></p>
            <ul class="list-group">
              ${existingTitles.map((title) => `<li class="list-group-item">${title}</li>`).join('')}
            </ul>
          </div>
        `;
        return await showConfirmModal({
          messageHtml,
          confirmText: 'Mantener chat existente',
        });
      }

      let messageHtml = '';
      const personalDocsToShare = docsToAppendToSession.filter((doc) => doc && doc.is_user_document);
      if (isCollaborativeActiveChat && personalDocsToShare.length) {
        messageHtml += `
          <div class="alert alert-warning mb-3" role="alert">
            <strong>Vas a compartir contenido de tu biblioteca personal.</strong>
            <p class="mb-2">Al confirmar, ${personalDocsToShare.length === 1 ? 'este archivo quedará disponible' : 'estos archivos quedarán disponibles'} para todos los miembros del chat como contexto compartido. Podrán abrirlos desde esta conversación y usar funciones del Abogado Virtual sobre ese contenido dentro de este chat.</p>
            <ul class="mb-0">
              ${personalDocsToShare.map((doc) => `<li>${escapeHtml(doc.page_title || doc.path)}</li>`).join('')}
            </ul>
          </div>
        `;
      }
      if (docsToAppendToSession.length) {
        messageHtml += `
          <p>¿Desea agregar ${docsToAppendToSession.length === 1 ? 'este documento' : 'estos documentos'} al chat actual?</p>
          <div class="mt-3">
            <p><strong>Documentos nuevos:</strong></p>
            <ul class="list-group">
              ${docsToAppendToSession.map((doc) => `<li class="list-group-item">${escapeHtml(doc.page_title || doc.path)}</li>`).join('')}
            </ul>
          </div>
        `;
      }
      if (warnedDocs.length) {
        messageHtml += `
          <div class="mt-3">
            <p><strong>Documentos fuera de contexto:</strong></p>
            <p>Estos documentos estuvieron en el chat, pero ya no están en el contexto actual de la IA. Si confirma, se volverán a inyectar en su próximo mensaje.</p>
            <ul class="list-group">
              ${warnedDocs.map((doc) => `<li class="list-group-item">${escapeHtml(doc.page_title || doc.path)}</li>`).join('')}
            </ul>
          </div>
        `;
      }
      if (alreadyInContextDocs.length) {
        messageHtml += `
          <div class="mt-3">
            <p><strong>Documentos ya en contexto (no se duplicarán):</strong></p>
            <ul class="list-group">
              ${alreadyInContextDocs.map((doc) => `<li class="list-group-item">${escapeHtml(doc.page_title || doc.path)}</li>`).join('')}
            </ul>
          </div>
        `;
      }

      const confirmText = warnedDocs.length && !docsToAppendToSession.length
        ? 'Volver a agregar al contexto'
        : 'Agregar al chat actual';

      const requiresPersonalDocumentSharingConsent = isCollaborativeActiveChat
        && personalDocsToShare.length > 0;
      const shouldShowConfirmation = additionOptions.preconfirmed !== true
        || requiresPersonalDocumentSharingConsent;
      const confirmed = shouldShowConfirmation
        ? await showConfirmModal({
          messageHtml,
          confirmText: additionOptions.preconfirmed === true
            ? 'Compartir y agregar al chat'
            : confirmText,
          cancelText: additionOptions.preconfirmed === true ? 'Cancelar' : 'Crear nuevo chat',
        })
        : true;
      if (!confirmed) return false;
      if (exceedsCurrentChatCapacity) return rejectCatalogCapacity();

      let documentAdditionCheckpointVersion = null;
      if (
        docsToAppendToSession.length
        && typeof this.$refs.chatLayout.captureDocumentAdditionCheckpoint === 'function'
      ) {
        this.$refs.chatLayout.captureDocumentAdditionCheckpoint(activeChat, {
          preserveExisting: usesLazyCatalog,
        });
        if (typeof this.$refs.chatLayout.currentDocumentAdditionCheckpointVersion === 'function') {
          documentAdditionCheckpointVersion = this.$refs.chatLayout.currentDocumentAdditionCheckpointVersion(
            activeChat
          );
        }
      }

      if (docsToAppendToSession.length) {
        const currentDocs = extractDocsFromSession();
        const currentMap = new Map(currentDocs.map((doc) => [doc.key, doc]));
        for (const doc of docsToAppendToSession) {
          if (currentMap.has(doc.key)) continue;
          currentDocs.push(doc);
          currentMap.set(doc.key, doc);
        }
        applySessionDocs(currentDocs);
        if (is_law !== undefined && is_law !== null && !docsToAppendToSession.some((doc) => doc.is_user_document)) {
          activeChat.is_law = !!is_law;
        }

        docsToAppendToSession.forEach((doc) => {
          state.push({
            doc_id: doc.key,
            path: doc.path,
            page_title: doc.page_title || '',
            is_user_document: doc.is_user_document,
            ...(usesLazyCatalog
              ? { context_representation: LAZY_DOCUMENT_CONTEXT_REPRESENTATION }
              : {}),
            added_during: hasMessages ? 'added_later' : 'initial',
            is_out_of_context: false,
            pending_reinjection: false,
            injection_message_id: null,
            out_of_context_at: null,
            last_injected_at: null,
            key: doc.key,
          });
        });
      }

      if (usesLazyCatalog) {
        state.forEach((entry) => {
          entry.context_representation = LAZY_DOCUMENT_CONTEXT_REPRESENTATION;
          entry.pending_reinjection = false;
        });
        activeChat.pending_document_injections = Array.isArray(activeChat.pending_document_injections)
          ? activeChat.pending_document_injections.map((entry) => ({
            ...entry,
            force_reinject: false,
            catalog_binding_only: true,
          }))
          : [];
        if (hasMessages && docsToAppendToSession.length) {
          enqueuePendingInjections(docsToAppendToSession.map((doc) => ({
            ...doc,
            catalog_binding_only: true,
          })));
        }
      } else if (hasMessages && docsToInjectNextMessage.length) {
        enqueuePendingInjections(docsToInjectNextMessage);
      }

      activeChat.chat_documents_state = state;
      activeChat.suggestedQuestions = [];
      this.$refs.chatLayout.computeSuggestedQuestionsForPath();
      const hasPersistentUuid = !!(activeChat.uuid && String(activeChat.uuid).trim());
      if (hasPersistentUuid && typeof this.$refs.chatLayout.store === 'function') {
        this.$refs.chatLayout.store();
      }
      if (hasPersistentUuid && this.allSessions && this.allSessions[activeChat.uuid]) {
        const updatedSessions = { ...this.allSessions };
        updatedSessions[activeChat.uuid] = {
          ...updatedSessions[activeChat.uuid],
          path: activeChat.path,
          page_title: activeChat.page_title,
          is_user_document: activeChat.is_user_document,
          is_law: activeChat.is_law,
          is_multi_document: !!activeChat.is_multi_document,
          page_title_html: activeChat.page_title_html || null,
          chat_documents_state: Array.isArray(activeChat.chat_documents_state) ? activeChat.chat_documents_state : [],
          pending_document_injections: Array.isArray(activeChat.pending_document_injections) ? activeChat.pending_document_injections : [],
        };
        this.allSessions = updatedSessions;
      }
      // The authenticated chat turn validates reader capability and creates
      // authoritative private-source bindings. Do not pre-commit a lazy
      // catalog through the untrusted sync migration path.
      const persistenceResult = usesLazyCatalog
        ? { availabilityHandled: false }
        : await persistDocumentMetadataIfSyncEnabled(documentAdditionCheckpointVersion);
      if (persistenceResult && persistenceResult.typedErrorCode) {
        if (typeof this.$refs.chatLayout.rollbackDocumentAdditionCheckpoint === 'function') {
          this.$refs.chatLayout.rollbackDocumentAdditionCheckpoint(
            String(activeChat.uuid || ''),
            documentAdditionCheckpointVersion,
          );
        }
        const typedMessage = persistenceResult.typedErrorCode === 'document_reader_unavailable'
          ? 'Los documentos no se agregaron porque Lexius no puede consultarlos en este momento. Inténtalo de nuevo más tarde.'
          : (persistenceResult.typedErrorCode === 'chat_document_metadata_conflict'
            ? 'No se pudieron guardar los documentos porque los metadatos del chat no se pudieron reconciliar. Inténtalo de nuevo.'
            : `Este chat admite hasta ${CHAT_DOCUMENT_CATALOG_LIMIT} documentos. No se agregó ningún documento nuevo.`);
        this.$refs.chatLayout.presentDocumentAvailabilityNotice({
          tone: 'error',
          title: 'No se agregaron los documentos',
          detail: typedMessage,
        }, activeChat);
        return { handled: true, attached: false };
      }

      return true;
    },

    async sessionSelected(sessionKey = null, path = null, page_title = null, is_user_document = null, is_law = null) {
      console.log('sessionSelected', sessionKey, path, page_title, is_user_document, is_law)

      // If no session key is provided, check if we should add to existing chat
      if (!sessionKey && path) {
        const initializeLargeEmptyCatalog = (
          Array.isArray(path)
          && path.length > CHAT_DOCUMENT_LAZY_THRESHOLD
          && this.isChatSessionTrulyEmpty(this.$refs.chatLayout?.currentSession)
        );
        const added = await this.checkAndHandleDocumentAddition(
          path,
          page_title,
          is_user_document,
          is_law,
          initializeLargeEmptyCatalog
            ? { preconfirmed: true, allowEmptyChat: true }
            : {}
        );
        if ((added && typeof added === 'object') ? added.handled !== false : !!added) return;
      }

      // If offline with sync enabled and the chat does not exist locally, create a minimal stub
      try {
        if (sessionKey && window.sync_enabled === true && typeof navigator !== 'undefined' && navigator.onLine === false) {
          const key = 'lexius_virtual_lawyer_chats';
          let chats = localStorage.getItem(key) ?? '{}';
          chats = JSON.parse(chats);
          if (!chats[sessionKey]) {
            chats[sessionKey] = {
              uuid: sessionKey,
              title: 'Chat',
              data: [],
              created_at: null,
              updated_at: null,
              path: null,
              page_title: null,
              is_user_document: null,
              is_law: false,
              is_multi_document: false,
              page_title_html: null,
              suggestedQuestions: [],
              is_call_log: false,
              is_pinned: false,
              chat_documents_state: [],
              pending_document_injections: [],
              lastError: true,
              errorMessage: 'Sin conexión a Internet. No se pudo cargar esta conversación. Conéctate e inténtalo de nuevo.',
            };
            try { localStorage.setItem(key, JSON.stringify(chats)); } catch (e) { }
          }
        }
      } catch (e) { }

      // Avoid duplicate fetching: ChatLayout owns loading the conversation
      // when sync is enabled. This prevents multiple /chats/get calls.
      const switchResult = await this.$refs.chatLayout.switchSession(
        sessionKey,
        path,
        page_title,
        is_user_document,
        is_law,
        ...(this.allSessions?.[sessionKey]?.is_call_log
          ? [{ fallbackSession: this.allSessions[sessionKey] }]
          : []),
      )
      if (switchResult && switchResult.stale === true) return
      this.activeSession = sessionKey
      try {
        if (this.allSessions && this.allSessions[sessionKey]) {
          this.applySessionPatch(sessionKey, { unread_count: 0 });
        }
      } catch (e) { }
      this.ensureActiveSessionInSidebar(sessionKey)
      this.revealSessionInSidebar(sessionKey)
    },

    handleStartAudioCall() {
      // Hide the main chat modal first - This is now handled by audioCallHandler.prepareAndShowModal()
      // const chatModal = document.getElementById('chatModal');
      // if (chatModal) {
      //   const bootstrapModal = bootstrap.Modal.getInstance(chatModal);
      //   if (bootstrapModal) {
      //     bootstrapModal.hide();
      //   }
      // }

      if (window.audioCallHandler && typeof window.audioCallHandler.prepareAndShowModal === 'function') {
        window.audioCallHandler.prepareAndShowModal();
        // audioCallHandler.initialize() will be called by the 'shown.bs.modal' event listener (added in mounted())
        // once the audioCallModal is fully shown.
      } else {
        console.error("[App.js] AudioCallHandler or prepareAndShowModal not available.");
        // Fallback to old behavior: directly show the modal
        const audioCallModal = document.getElementById('audioCallModal');
        if (audioCallModal) {
          const audioCallBootstrapModal = bootstrap.Modal.getInstance(audioCallModal) || new bootstrap.Modal(audioCallModal);
          audioCallBootstrapModal.show();
          // The 'shown.bs.modal' listener will handle initialization.
        }
      }
    },

    async deleteSession(sessionKey) {
      // If server-backed sync is enabled, request a soft delete on the backend
      if (window.sync_enabled === true) {
        try {
          const r = await fetch('/chats/delete', {
            method: 'POST',
            headers: (function () {
              try {
                const deviceId = new DeviceUUID().get();
                return { 'Content-Type': 'application/json', 'device-uuid': deviceId };
              } catch (e) {
                return { 'Content-Type': 'application/json' };
              }
            })(),
            body: JSON.stringify({ uuid: sessionKey })
          });
          if (r.ok) {
            // Remove locally from in-memory map for immediate UX, then refresh from server
            const map = { ...this.allSessions };
            delete map[sessionKey];
            this.allSessions = map;
            // If the deleted chat is active, clear the active session and reset chat view
            if (sessionKey === this.activeSession) {
              this.activeSession = null;
              this.$refs.chatLayout && this.$refs.chatLayout.switchSession(null);
            }
            // Refresh list (server is source of truth)
            this.updateSessions();
            return;
          }
        } catch (e) {
          // Fall through to local deletion if server call fails
        }
      }

      // Local fallback (pre-sync or server error)
      try {
        let chats = localStorage.getItem('lexius_virtual_lawyer_chats') ?? '{}';
        chats = JSON.parse(chats);
        delete chats[sessionKey];
        localStorage.setItem('lexius_virtual_lawyer_chats', JSON.stringify(chats));
      } catch (error) {
        if (!this.allSessions[sessionKey]?.call_transcript_pending_save) throw error;
      }
      const remainingSessions = { ...this.allSessions };
      delete remainingSessions[sessionKey];
      this.allSessions = remainingSessions;
      if (sessionKey === this.activeSession) {
        this.activeSession = null;
        this.$refs.chatLayout && this.$refs.chatLayout.switchSession(null);
      }
      this.updateSessions();
    },

    async leaveSession(sessionKey) {
      const uuid = String(sessionKey || '').trim();
      if (!uuid) return;
      try {
        const r = await fetch('/chats/collaboration/participants/leave', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uuid })
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          alert(data.message || 'No se pudo abandonar el chat.');
          return;
        }
        this.handleCollaborationRemoved(uuid);
        if (this.$refs.chatLayout && this.$refs.chatLayout.currentSession && String(this.$refs.chatLayout.currentSession.uuid || '') === uuid) {
          this.$refs.chatLayout.switchSession(null);
        }
        if (window.sync_enabled === true) {
          this.updateSessions({ page: 1, append: false });
        }
      } catch (e) {
        alert('No se pudo abandonar el chat.');
      }
    },

    applySessionPatch(sessionKey, patch) {
      if (!sessionKey || !patch || typeof patch !== 'object') return;
      const map = { ...this.allSessions };
      if (!map[sessionKey]) {
        const built = this.buildSidebarSessionFromCurrentSession(sessionKey, null, { uuid: sessionKey, data: [] });
        map[sessionKey] = built || {
          uuid: sessionKey,
          title: 'Chat',
          data: [],
          suggestedQuestions: [],
          chat_documents_state: [],
          pending_document_injections: [],
        };
      }
      map[sessionKey] = { ...map[sessionKey], ...patch };
      this.allSessions = map;

      try {
        const key = 'lexius_virtual_lawyer_chats';
        let chats = localStorage.getItem(key) ?? '{}';
        let parsed = {};
        try { parsed = JSON.parse(chats); } catch (e) { parsed = {}; }
        if (!parsed[sessionKey]) {
          parsed[sessionKey] = { uuid: sessionKey, data: [] };
        }
        parsed[sessionKey] = { ...parsed[sessionKey], ...patch };
        localStorage.setItem(key, JSON.stringify(parsed));
      } catch (e) { }

      try {
        if (sessionKey === this.activeSession && this.$refs.chatLayout && this.$refs.chatLayout.currentSession) {
          Object.assign(this.$refs.chatLayout.currentSession, patch);
          if (typeof this.$refs.chatLayout.store === 'function') {
            this.$refs.chatLayout.store();
          }
        }
      } catch (e) { }
    },

    escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    prepareHtmlForExport(html) {
      if (!html || typeof html !== 'string') {
        return html;
      }
      if (typeof convertLinksToAbsoluteOpenPath === 'function') {
        return convertLinksToAbsoluteOpenPath(html);
      }
      return html;
    },

    encodeExportHref(pathValue) {
      const rawPath = String(pathValue || '').trim();
      if (!rawPath) return '';
      const queryIndex = rawPath.indexOf('?');
      const pathname = queryIndex >= 0 ? rawPath.slice(0, queryIndex) : rawPath;
      const search = queryIndex >= 0 ? rawPath.slice(queryIndex) : '';

      let encodedPathname = pathname;
      try {
        encodedPathname = encodeURI(pathname);
      } catch (e) { }

      return `${encodedPathname}${search}`;
    },

    buildExportDocumentHref(pathValue, isUserDocument, pageTitle = '') {
      let rawPath = String(pathValue || '').trim();
      if (!rawPath) return '';

      try {
        rawPath = decodeURIComponent(rawPath);
      } catch (e) { }

      if (/^https?:\/\//i.test(rawPath)) {
        return rawPath;
      }

      const normalizedTitle = String(pageTitle || '').trim();
      const corporateScope = rawPath.includes('scope=corporate') ? 'corporate' : null;
      const looksLikeUserUpload = (
        isUserDocument
        || rawPath.startsWith('/get_user_upload')
        || rawPath.startsWith('get_user_upload?')
      );

      if (looksLikeUserUpload) {
        const chatUuid = corporateScope ? '' : this.getActiveChatUuidForRecording();
        if (typeof buildUserUploadHref === 'function') {
          return buildUserUploadHref(rawPath, normalizedTitle || 'Documento', corporateScope, chatUuid);
        }

        const normalizedUserPath = rawPath.replace(/^\/+/, '');
        if (!normalizedUserPath) return '';
        let href = `/get_user_upload?filename=${encodeURIComponent(normalizedUserPath)}&original_filename=${encodeURIComponent(normalizedTitle || 'Documento')}`;
        if (chatUuid) {
          href += `&chat_uuid=${encodeURIComponent(chatUuid)}`;
        }
        return `${href}&get_raw=true`;
      }

      if (rawPath.startsWith('/abrir_documento') || rawPath.startsWith('abrir_documento?')) {
        return rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
      }

      if (rawPath.startsWith('/Legislaci') || rawPath.startsWith('Legislaci')) {
        const normalizedLawPath = rawPath
          .replace(/^\/+/, '/')
          .replace('/Legislación_dev/', '/Legislación/')
          .replace('/Legislación-dev/', '/Legislación/')
          .replace('/Legislacion_dev/', '/Legislación/')
          .replace('/Legislacion-dev/', '/Legislación/');
        return this.encodeExportHref(normalizedLawPath.startsWith('/') ? normalizedLawPath : `/${normalizedLawPath}`);
      }

      const lawMatch = rawPath.match(/(?:^|\/)Legislaci(?:ón|on)(?:_dev|-dev)?\/(.+)$/i);
      if (lawMatch && lawMatch[1]) {
        return this.encodeExportHref(`/Legislación/${lawMatch[1].replace(/^\/+/, '')}`);
      }

      let normalizedPath = this.normalizeNonUserDocumentIdentityPath(rawPath);
      if (!normalizedPath) return '';

      const documentsFolder = (typeof DOCUMENTS_FOLDER === 'string' ? DOCUMENTS_FOLDER.trim() : '')
        .replace(/^\/+/, '')
        .replace(/\/+$/, '');
      const documentsFolderToken = documentsFolder ? `/${documentsFolder}/` : '';
      if (documentsFolderToken) {
        const folderIndex = normalizedPath.lastIndexOf(documentsFolderToken);
        if (folderIndex >= 0) {
          normalizedPath = normalizedPath.slice(folderIndex + documentsFolderToken.length);
        }
      }

      normalizedPath = normalizedPath.replace(/^\/+/, '');
      if (!normalizedPath) return '';
      return this.encodeExportHref(`/${normalizedPath}`);
    },

    sanitizeFileName(value, extension = '') {
      const maxLength = 130;
      const fallback = `Conversacion LEXIUS ${window.capitalized_country || ''}`.trim();
      const normalizedExtension = String(extension || '');
      const cleaned = String(value == null ? '' : value)
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const safeBase = cleaned || fallback;
      const maxBaseLength = Math.max(1, maxLength - normalizedExtension.length);
      const truncatedBase = safeBase.slice(0, maxBaseLength).trim();
      return `${truncatedBase || fallback.slice(0, maxBaseLength).trim() || 'Chat'}${normalizedExtension}`;
    },

    showExportToast(html, isError = false) {
      try {
        if (typeof window !== 'undefined' && typeof window.showToastMessage === 'function') {
          window.showToastMessage(html);
          return;
        }
      } catch (e) { }
      if (isError) {
        alert('No se pudo exportar el chat en PDF. Por favor, inténtelo de nuevo.');
      }
    },

    readStoredSessions() {
      try {
        const raw = localStorage.getItem('lexius_virtual_lawyer_chats') ?? '{}';
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
      } catch (e) {
        return {};
      }
    },

    getStoredSession(sessionKey) {
      const stored = this.readStoredSessions();
      return stored && stored[sessionKey] ? stored[sessionKey] : null;
    },

    parseMaybeJsonArray(value) {
      if (Array.isArray(value)) return value;
      if (value === undefined || value === null || value === '') return [];
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
          try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed) ? parsed : [parsed];
          } catch (e) { }
        }
        return [trimmed];
      }
      return [value];
    },

    coerceBoolean(value) {
      if (value === undefined) return undefined;
      if (value === null) return null;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') {
        const trimmed = value.trim().toLowerCase();
        if (!trimmed) return false;
        if (["true", "1", "sí", "si", "yes"].includes(trimmed)) return true;
        if (["false", "0", "no"].includes(trimmed)) return false;
        if (["null", "none", "undefined"].includes(trimmed)) return null;
        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            return this.coerceBoolean(parsed);
          } catch (e) {
            return true;
          }
        }
        return true;
      }
      if (Array.isArray(value)) {
        return value.some((item) => this.coerceBoolean(item));
      }
      if (typeof value === 'object') return true;
      return Boolean(value);
    },

    preserveSourceBinding(entry) {
      return preserveSourceBinding(entry);
    },

    normalizeNonUserDocumentIdentityPath(pathValue) {
      let normalizedPath = String(pathValue || '').trim();
      if (!normalizedPath) return normalizedPath;
      if (normalizedPath.startsWith('./')) normalizedPath = normalizedPath.slice(2);
      normalizedPath = normalizedPath.replace(/^\/+/, '');

      const documentsFolder = (typeof DOCUMENTS_FOLDER === 'string' ? DOCUMENTS_FOLDER.trim() : '')
        .replace(/^\/+/, '')
        .replace(/\/+$/, '');
      if (
        documentsFolder &&
        (normalizedPath === documentsFolder || normalizedPath.startsWith(`${documentsFolder}/`))
      ) {
        normalizedPath = normalizedPath.slice(documentsFolder.length).replace(/^\/+/, '');
      }

      return normalizedPath;
    },

    buildDocumentKey(pathValue, isUserDocument, scopeHint = '', sourceBinding = null) {
      const originalPath = String(pathValue || '').trim();
      let normalizedPath = '';
      try {
        normalizedPath = decodeURIComponent(originalPath);
      } catch (e) {
        normalizedPath = originalPath;
      }

      const effectiveIsUserDocument = !!isUserDocument
        || normalizedPath.includes('/get_user_upload');
      if (effectiveIsUserDocument) {
        let filename = '';
        try {
          const parsed = new URL(normalizedPath, window.location.origin);
          filename = parsed.searchParams.get('filename') || '';
        } catch (e) { }
        if (!filename && normalizedPath.includes('filename=')) {
          try {
            filename = normalizedPath.split('filename=', 2)[1].split('&', 1)[0];
          } catch (e) {
            filename = '';
          }
        }
        normalizedPath = (filename || normalizedPath.split('/').pop() || '').trim();
      } else {
        let extracted = normalizedPath;
        try {
          const parsed = new URL(normalizedPath, window.location.origin);
          const pathname = (parsed.pathname || '').trim();
          const canonicalQuery = Array.from(parsed.searchParams.entries())
            .sort(([keyA, valueA], [keyB, valueB]) => {
              if (keyA === keyB) return String(valueA).localeCompare(String(valueB));
              return keyA.localeCompare(keyB);
            })
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
          const pathParam = parsed.searchParams.get('path');
          if (pathParam) {
            try {
              extracted = decodeURIComponent(pathParam);
            } catch (e) {
              extracted = pathParam;
            }
          } else if (canonicalQuery) {
            extracted = pathname && pathname !== '/'
              ? `${pathname}?${canonicalQuery}`
              : `?${canonicalQuery}`;
          } else {
            if (pathname && pathname !== '/') extracted = pathname;
          }
        } catch (e) {
          if (normalizedPath.includes('path=')) {
            try {
              extracted = normalizedPath.split('path=', 2)[1].split('&', 1)[0];
              extracted = decodeURIComponent(extracted);
            } catch (err) {
              extracted = normalizedPath;
            }
          }
        }
        normalizedPath = String(extracted || '').trim();
        normalizedPath = this.normalizeNonUserDocumentIdentityPath(normalizedPath);
      }

      if (effectiveIsUserDocument) {
        const scope = privateDocumentScopeFromLocator(originalPath, scopeHint);
        const binding = sourceBinding && typeof sourceBinding === 'object'
          ? sourceBinding
          : {};
        const sourceId = String(binding.source_id || '').trim();
        const sourceScopeId = String(binding.source_scope_id || '').trim();
        const sourceRevision = String(binding.source_revision_hash || '').trim();
        const sourceKind = String(binding.source_kind || '').trim();
        if (
          ['personal', 'corporate'].includes(scope)
          && sourceId
          && sourceScopeId
          && sourceRevision
          && sourceKind
        ) return `source|${sourceId}`;
        return scope === 'corporate'
          ? `1|corporate|${normalizedPath}`
          : `1|${normalizedPath}`;
      }
      return `0|${normalizedPath}`;
    },

    normalizeChatDocumentsStatePayload(rawState) {
      let state = rawState;
      if (typeof state === 'string') {
        const trimmed = state.trim();
        if (!trimmed) return [];
        try {
          state = JSON.parse(trimmed);
        } catch (e) {
          return [];
        }
      }
      if (!Array.isArray(state)) return [];

      const normalized = [];
      const indexByKey = new Map();
      const unboundIndexByLocator = new Map();
      for (const entry of state) {
        if (!entry || typeof entry !== 'object') continue;
        const path = (entry.path == null ? '' : String(entry.path)).trim();
        if (!path) continue;
        const coercedIsUser = this.coerceBoolean(entry.is_user_document);
        const isUser = path.includes('/get_user_upload')
          ? true
          : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
        const scopeHint = entry.source_scope_kind || entry.scope || entry.user_document_scope;
        const locatorKey = this.buildDocumentKey(path, isUser, scopeHint);
        const key = this.buildDocumentKey(
          path,
          isUser,
          scopeHint,
          entry,
        );
        const normalizedEntry = {
          doc_id: entry.doc_id || key,
          path,
          page_title: entry.page_title == null ? '' : String(entry.page_title),
          is_user_document: isUser,
          ...this.preserveSourceBinding(entry),
          ...(isLazyCatalogDocument(entry)
            ? { context_representation: LAZY_DOCUMENT_CONTEXT_REPRESENTATION }
            : {}),
          added_during: entry.added_during === 'added_later' ? 'added_later' : 'initial',
          is_out_of_context: !!entry.is_out_of_context,
          pending_reinjection: !!entry.pending_reinjection,
          injection_message_id: entry.injection_message_id || null,
          out_of_context_at: entry.out_of_context_at || null,
          last_injected_at: entry.last_injected_at || null,
          key,
        };
        let existingIndex = indexByKey.get(key);
        if (!Number.isInteger(existingIndex) && key !== locatorKey) {
          existingIndex = unboundIndexByLocator.get(locatorKey);
        }
        if (Number.isInteger(existingIndex)) {
          normalized[existingIndex] = mergeMonotonicDocumentProvenance(
            normalized[existingIndex],
            normalizedEntry,
          );
          indexByKey.set(key, existingIndex);
          if (key !== locatorKey) unboundIndexByLocator.delete(locatorKey);
          continue;
        }
        indexByKey.set(key, normalized.length);
        if (key === locatorKey) unboundIndexByLocator.set(locatorKey, normalized.length);
        normalized.push(normalizedEntry);
      }
      return normalized;
    },

    normalizeContextDocuments(rawDocs) {
      if (!Array.isArray(rawDocs)) return [];
      const normalized = [];
      const seen = new Set();

      rawDocs.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const path = String(item.path || '').trim();
        if (!path) return;
        const coercedIsUser = this.coerceBoolean(item.is_user_document);
        const isUserDocument = path.includes('/get_user_upload')
          ? true
          : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
        const key = this.buildDocumentKey(
          path,
          isUserDocument,
          item.source_scope_kind || item.scope || item.user_document_scope,
        );
        if (seen.has(key)) return;
        seen.add(key);
        normalized.push({
          key,
          path,
          page_title: String(item.page_title || '').trim(),
          is_user_document: isUserDocument,
        });
      });

      return normalized;
    },

    normalizeSessionDocuments(session) {
      const docs = [];
      const seen = new Set();
      const appendDoc = (doc) => {
        if (!doc || typeof doc !== 'object') return;
        const path = String(doc.path || '').trim();
        const rawTitle = String(doc.title || doc.page_title || doc.path || '').trim();
        if (!path && !rawTitle) return;
        const coercedIsUser = this.coerceBoolean(doc.is_user_document);
        const isUserDocument = path.includes('/get_user_upload')
          ? true
          : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
        const key = path
          ? this.buildDocumentKey(
            path,
            isUserDocument,
            doc.source_scope_kind || doc.scope || doc.user_document_scope,
            doc,
          )
          : `${isUserDocument ? 1 : 0}|${rawTitle}`;
        if (seen.has(key)) return;
        seen.add(key);
        docs.push({
          title: rawTitle || path || 'Documento',
          path,
          is_user_document: isUserDocument,
          added_during: doc.added_during === 'added_later' ? 'added_later' : 'initial',
        });
      };

      const normalizedState = this.normalizeChatDocumentsStatePayload(
        session?.chat_documents_state,
      );
      normalizedState.forEach((doc) => appendDoc(doc));

      const titles = this.parseMaybeJsonArray(session?.page_title);
      const paths = this.parseMaybeJsonArray(session?.path);
      const userFlags = this.parseMaybeJsonArray(session?.is_user_document);
      const maxLength = Math.max(titles.length, paths.length, userFlags.length);

      for (let index = 0; index < maxLength; index += 1) {
        const title = titles[index] ?? titles[0] ?? '';
        const path = paths[index] ?? paths[0] ?? '';
        const rawIsUser = userFlags[index] ?? userFlags[0] ?? false;
        const alignedState = normalizedState[index];
        const alignedIsUser = alignedState
          ? !!alignedState.is_user_document
          : null;
        const legacyIsUser = String(path || '').includes('/get_user_upload')
          ? true
          : this.coerceBoolean(rawIsUser) === true;
        const stateMatchesLegacyRow = !!(
          alignedState
          && String(alignedState.path || '').trim() === String(path || '').trim()
          && alignedIsUser === legacyIsUser
        );
        appendDoc({
          title,
          path,
          is_user_document: rawIsUser,
          added_during: 'initial',
          ...(stateMatchesLegacyRow ? this.preserveSourceBinding(alignedState) : {}),
        });
      }

      return docs;
    },

    resolveExportTimezone() {
      const locale = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'es-ES';
      let timeZone = 'UTC';
      try {
        const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (resolved) timeZone = resolved;
      } catch (e) { }

      let offsetLabel = 'GMT';
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone,
          timeZoneName: 'shortOffset',
          hour: '2-digit',
          minute: '2-digit',
        }).formatToParts(new Date());
        const tzName = parts.find((part) => part.type === 'timeZoneName');
        if (tzName && tzName.value) {
          offsetLabel = tzName.value;
        }
      } catch (e) { }

      const displayLabel = timeZone && timeZone !== offsetLabel
        ? `${timeZone} (${offsetLabel})`
        : offsetLabel;

      return { locale, timeZone, offsetLabel, displayLabel };
    },

    formatTimestampForExport(timestamp, timeZone, locale) {
      if (timestamp === undefined || timestamp === null || timestamp === '') {
        return null;
      }

      let date = null;
      if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp);
      }

      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null;
      }

      try {
        return new Intl.DateTimeFormat(locale || 'es-ES', {
          timeZone: timeZone || 'UTC',
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(date);
      } catch (e) {
        return date.toLocaleString();
      }
    },

    stripLexiusDocFilenameHint(text) {
      if (!text || typeof text !== 'string') {
        return text;
      }
      return text.replace(/^[\t ]*Nombre\s+de\s+archivo(?:\s+sugerido)?\s*:[^\n]*\n?/gim, '').replace(/\n{3,}/g, '\n\n');
    },

    transformLexiusDocHtmlForExport(html, content) {
      const extractor = (typeof window !== 'undefined' && typeof window.extractLexiusDocBlocks === 'function')
        ? window.extractLexiusDocBlocks
        : null;
      const titleResolver = (typeof window !== 'undefined' && typeof window.deriveLexiusDocTitle === 'function')
        ? window.deriveLexiusDocTitle
        : null;

      if (!extractor && !titleResolver) {
        return html;
      }

      const blocks = extractor ? extractor(content) : [];
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const cardNodes = Array.from(doc.querySelectorAll('.lexiusdoc-card'));
        const codeNodes = cardNodes.length ? [] : Array.from(doc.querySelectorAll('pre code.language-lexiusdoc'));
        if (!cardNodes.length && !codeNodes.length) {
          return html;
        }

        const replaceNode = (target, index, fallbackRaw = '') => {
          if (!target || !target.parentNode) return;
          const block = blocks[index] || {};
          const fallbackTitle = (() => {
            if (typeof titleResolver === 'function') {
              try {
                return titleResolver(block.raw || fallbackRaw || '') || 'Documento generado';
              } catch (e) { }
            }
            const firstLine = String(block.raw || fallbackRaw || '')
              .split(/\r?\n/)
              .map((line) => line.trim())
              .find((line) => !!line);
            return firstLine ? firstLine.replace(/^#{1,6}\s+/, '').trim() : 'Documento generado';
          })();
          const bodyHtml = block.htmlWithoutTitle || block.html || this.escapeHtml(block.rawWithoutTitle || block.raw || fallbackRaw || '').replace(/\n/g, '<br>');

          const wrapper = doc.createElement('section');
          wrapper.classList.add('lexiusdoc-export');

          const label = doc.createElement('div');
          label.classList.add('lexiusdoc-export__label');
          label.textContent = 'Documento generado';

          const title = doc.createElement('h4');
          title.classList.add('lexiusdoc-export__title');
          title.textContent = block.title || fallbackTitle || 'Documento generado';

          const body = doc.createElement('div');
          body.classList.add('lexiusdoc-export__body');
          body.innerHTML = bodyHtml;

          wrapper.appendChild(label);
          wrapper.appendChild(title);
          wrapper.appendChild(body);
          target.parentNode.replaceChild(wrapper, target);
        };

        cardNodes.forEach((cardNode, index) => replaceNode(cardNode, index));
        codeNodes.forEach((codeNode, index) => replaceNode(codeNode.closest('pre') || codeNode, index, codeNode.textContent || ''));

        return doc.body.innerHTML;
      } catch (e) {
        return html;
      }
    },

    renderMarkdownForExport(text) {
      const baseText = this.stripLexiusDocFilenameHint(String(text || ''));
      let processedMarkdown = baseText;
      try {
        if (typeof processMarkdownContent === 'function') {
          processedMarkdown = processMarkdownContent(baseText);
        }
      } catch (e) { }

      let html = this.escapeHtml(baseText).replace(/\n/g, '<br>');
      try {
        html = marked.parse(processedMarkdown);
        if (typeof processConvertedHTML === 'function') {
          html = processConvertedHTML(html, baseText);
        }
        if (/`{3,}lexiusdoc/i.test(baseText) || html.includes('lexiusdoc-card')) {
          html = this.transformLexiusDocHtmlForExport(html, baseText);
        }
        html = sanitizeRichHtml(html, baseText);
      } catch (e) {
        html = escapePlaintextHtml(baseText);
      }

      return sanitizeRichHtml(this.prepareHtmlForExport(html), baseText);
    },

    normalizeConversationForExport(payload) {
      const conversation = Array.isArray(payload?.conversation) ? payload.conversation : [];
      const messages = [];

      conversation.forEach((message) => {
        if (!message || !message.role) return;
        if (message.role === 'tool') return;
        if (message.role === 'assistant') {
          if (!message.content || (message.tool_calls && message.tool_calls.length)) return;
          messages.push({
            sender: 'bot',
            text: message.content,
            timestamp: message.timestamp || null,
          });
          return;
        }
        if (message.role === 'system') {
          if (!message.content) return;
          messages.push({
            sender: 'system',
            content: message.content,
            timestamp: message.timestamp || null,
          });
          return;
        }
        if (message.role === 'user') {
          if (!message.content || message.chat_document_injection === true) return;
          messages.push({
            sender: 'user',
            text: message.content,
            timestamp: message.timestamp || null,
            contextDocuments: this.normalizeContextDocuments(
              message.context_documents || message.contextDocuments || message.chat_context_documents
            ),
          });
        }
      });

      if (payload?.was_cancelled === true) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.sender === 'user') {
          messages.push({
            sender: 'bot',
            text: '',
            timestamp: null,
            cancelled: true,
            isSyntheticExportState: true,
          });
        }
      }

      return messages;
    },

    async waitForExportUiPaint() {
      await this.$nextTick();
      await new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        const timeoutId = setTimeout(finish, 120);
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => {
            clearTimeout(timeoutId);
            finish();
          });
          return;
        }
        clearTimeout(timeoutId);
        setTimeout(finish, 0);
      });
    },

    getExportLogoUrl() {
      const fallback = '/static/images/lexius-transparent-logo-1.png';
      try {
        const logoCandidate = document.querySelector('img[alt^="Logo de LEXIUS"]');
        if (logoCandidate && (logoCandidate.currentSrc || logoCandidate.src)) {
          const logoUrl = new URL(logoCandidate.currentSrc || logoCandidate.src, window.location.origin);
          const countryLogo = `/static/images/Logo LEXIUS ${window.capitalized_country}.png`;
          if (
            logoUrl.origin === window.location.origin
            && [fallback, countryLogo].includes(decodeURIComponent(logoUrl.pathname))
          ) {
            return logoUrl.pathname;
          }
        }
      } catch (e) { }
      return fallback;
    },

    buildConversationExportHtml({ session, messages, timezoneInfo, logoUrl, fallbackNotice }) {
      const title = this.escapeHtml(session?.title || session?.page_title || 'Chat');
      const countryName = this.escapeHtml(window.capitalized_country || '');
      const chatType = session?.is_call_log ? 'Transcripción de llamada' : 'Chat de texto';
      const exportTimestamp = this.formatTimestampForExport(Date.now(), timezoneInfo.timeZone, timezoneInfo.locale);
      const firstTimestamp = this.formatTimestampForExport(
        session?.created_at || (messages[0] && messages[0].timestamp) || null,
        timezoneInfo.timeZone,
        timezoneInfo.locale
      );
      const docs = this.normalizeSessionDocuments(session);
      const metadataCards = [
        { label: 'Tipo', value: chatType },
        { label: 'Interacciones', value: String(messages.length) },
        { label: 'Zona horaria', value: timezoneInfo.displayLabel },
        { label: 'Exportado', value: exportTimestamp || 'Ahora' },
      ];

      if (firstTimestamp) {
        metadataCards.unshift({ label: 'Inicio', value: firstTimestamp });
      }

      const metadataHtml = metadataCards.map((item) => `
        <div class="meta-card">
          <span class="meta-label">${this.escapeHtml(item.label)}</span>
          <strong class="meta-value">${this.escapeHtml(item.value)}</strong>
        </div>
      `).join('');

      const docsHtml = docs.length ? `
        <section class="context-card">
          <div class="section-eyebrow">Contexto asociado</div>
          <h2>Documentos vinculados a la conversación</h2>
          <div class="doc-grid">
            ${docs.map((doc) => {
              const docHref = this.buildExportDocumentHref(doc.path, doc.is_user_document, doc.title);
              const titleHtml = docHref
                ? `<a class="doc-chip__title-link" href="${this.escapeHtml(docHref)}">${this.escapeHtml(doc.title)}</a>`
                : `<span class="doc-chip__title">${this.escapeHtml(doc.title)}</span>`;
              return `
                <div class="doc-chip">
                  <div class="doc-chip__title">${titleHtml}</div>
                  <div class="doc-chip__meta">${this.escapeHtml(doc.is_user_document ? 'Documento del usuario' : 'Base jurídica LEXIUS')}</div>
                  <div class="doc-chip__meta">${this.escapeHtml(doc.added_during === 'added_later' ? 'Añadido durante la conversación' : 'Contexto inicial')}</div>
                </div>
              `;
            }).join('')}
          </div>
        </section>
      ` : '';

      const noteHtml = fallbackNotice ? `
        <section class="notice-card">
          <strong>Nota:</strong> ${this.escapeHtml(fallbackNotice)}
        </section>
      ` : '';

      const messagesHtml = messages.map((message, index) => {
        const sender = message?.sender === 'user'
          ? 'user'
          : message?.sender === 'system'
            ? 'system'
            : 'assistant';
        const timestampLabel = this.formatTimestampForExport(message?.timestamp, timezoneInfo.timeZone, timezoneInfo.locale);
        const roleLabel = sender === 'user'
          ? 'Usuario'
          : sender === 'system'
            ? 'Sistema'
            : 'Abogado Virtual';
        let contentHtml = '';

        if (sender === 'system') {
          contentHtml = this.renderMarkdownForExport(message.content || '');
        } else {
          const baseText = message.text || (message.cancelled ? 'Respuesta cancelada.' : '');
          contentHtml = this.renderMarkdownForExport(baseText);
        }

        const contextDocuments = sender === 'user'
          ? this.normalizeContextDocuments(message.contextDocuments)
          : [];
        const contextDocumentsHtml = contextDocuments.map((doc) => {
          const docHref = this.buildExportDocumentHref(doc.path, doc.is_user_document, doc.page_title || doc.path);
          const label = doc.page_title || doc.path || 'Documento';
          if (!docHref) {
            return `<span class="message-doc">${this.escapeHtml(label)}</span>`;
          }
          return `<a class="message-doc" href="${this.escapeHtml(docHref)}">${this.escapeHtml(label)}</a>`;
        }).join('');

        return `
          <article class="message-card message-card--${sender}">
            <div class="message-header">
              <div class="message-header__main">
                <span class="message-role">${this.escapeHtml(roleLabel)}</span>
                <span class="message-index">#${index + 1}</span>
              </div>
              <span class="message-timestamp">${this.escapeHtml(timestampLabel || 'Hora no disponible')}</span>
            </div>
            <div class="message-body rich-text">
              ${contentHtml}
            </div>
            ${contextDocuments.length ? `
              <div class="message-docs">
                ${contextDocumentsHtml}
              </div>
            ` : ''}
          </article>
        `;
      }).join('');

      return `
        <!DOCTYPE html>
        <html lang="es">
        <head>
          <meta charset="UTF-8">
          <title>${title}</title>
          <style>
            @page {
              size: A4;
              margin: 18mm 14mm 18mm 14mm;
              @bottom-right {
                content: "Página " counter(page);
                color: #6b7280;
                font-size: 10px;
              }
            }
            :root {
              --ink: #172033;
              --muted: #5f6c83;
              --line: #d7dfeb;
              --panel: #f4f7fb;
              --brand: #1434a4;
              --brand-soft: #e9efff;
              --assistant: #ffffff;
              --user: #eef4ff;
              --system: #fff7e8;
              --accent: #d29b2e;
            }
            * {
              box-sizing: border-box;
            }
            body {
              margin: 0;
              color: var(--ink);
              font-family: "Helvetica Neue", Arial, sans-serif;
              background: #ffffff;
              font-size: 13px;
              line-height: 1.6;
            }
            .report-shell {
              position: relative;
            }
            .report-shell::before {
              content: "";
              position: absolute;
              inset: 0 0 auto 0;
              height: 170px;
              background:
                radial-gradient(circle at top right, rgba(210, 155, 46, 0.18), transparent 38%),
                linear-gradient(135deg, #0f1d4d, #2146c7 55%, #1a73d9 100%);
              border-radius: 0 0 28px 28px;
            }
            .report-content {
              position: relative;
              z-index: 1;
            }
            .report-header {
              color: #fff;
              padding: 18px 20px 22px;
            }
            .report-header-top {
              display: table;
              width: 100%;
              margin-bottom: 22px;
            }
            .report-header-top__logo,
            .report-header-top__meta {
              display: table-cell;
              vertical-align: middle;
            }
            .report-header-top__meta {
              text-align: right;
            }
            .report-logo {
              max-width: 180px;
              max-height: 52px;
            }
            .report-kicker {
              display: inline-block;
              margin-bottom: 8px;
              padding: 4px 10px;
              border-radius: 999px;
              background: rgba(255, 255, 255, 0.16);
              font-size: 10px;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }
            .report-title {
              margin: 0;
              font-size: 28px;
              line-height: 1.15;
              font-weight: 700;
            }
            .report-subtitle {
              margin: 10px 0 0;
              color: rgba(255, 255, 255, 0.85);
              font-size: 13px;
            }
            .meta-grid {
              display: table;
              width: 100%;
              border-spacing: 10px;
              margin: 18px -10px 0;
            }
            .meta-card {
              display: table-cell;
              width: 25%;
              background: #fff;
              color: var(--ink);
              border-radius: 16px;
              padding: 14px 16px;
              box-shadow: 0 10px 30px rgba(15, 29, 77, 0.12);
            }
            .meta-label {
              display: block;
              font-size: 10px;
              color: var(--muted);
              letter-spacing: 0.08em;
              text-transform: uppercase;
              margin-bottom: 6px;
            }
            .meta-value {
              display: block;
              font-size: 13px;
              line-height: 1.45;
            }
            .body-section {
              padding: 26px 4px 0;
            }
            .context-card,
            .notice-card {
              margin-bottom: 18px;
              padding: 18px 20px;
              border: 1px solid var(--line);
              border-radius: 18px;
              background: #fff;
            }
            .notice-card {
              background: #fffaf0;
              border-color: #f1d7a5;
            }
            .section-eyebrow {
              margin-bottom: 6px;
              color: var(--brand);
              font-size: 10px;
              font-weight: 700;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }
            .context-card h2 {
              margin: 0 0 14px;
              font-size: 18px;
              line-height: 1.2;
            }
            .doc-grid {
              font-size: 0;
            }
            .doc-chip {
              display: inline-block;
              vertical-align: top;
              width: calc(50% - 8px);
              margin: 0 8px 8px 0;
              padding: 12px 14px;
              border-radius: 14px;
              background: var(--panel);
              border: 1px solid #e5ebf5;
              font-size: 12px;
            }
            .doc-chip__title {
              font-weight: 700;
              margin-bottom: 4px;
            }
            .doc-chip__title-link {
              color: var(--ink);
              text-decoration: none;
            }
            .doc-chip__meta {
              color: var(--muted);
              font-size: 11px;
            }
            .doc-chip__meta + .doc-chip__meta {
              margin-top: 2px;
            }
            .conversation-section {
              margin-top: 6px;
            }
            .message-card {
              margin: 0 0 14px;
              padding: 16px 18px;
              border-radius: 18px;
              border: 1px solid var(--line);
              page-break-inside: avoid;
            }
            .message-card--assistant {
              background: var(--assistant);
            }
            .message-card--user {
              background: var(--user);
              border-color: #cddcff;
            }
            .message-card--system {
              background: var(--system);
              border-color: #f1d7a5;
            }
            .message-header {
              display: table;
              width: 100%;
              margin-bottom: 12px;
            }
            .message-header__main,
            .message-timestamp {
              display: table-cell;
              vertical-align: middle;
            }
            .message-timestamp {
              text-align: right;
              color: var(--muted);
              font-size: 11px;
              white-space: nowrap;
            }
            .message-role {
              display: inline-block;
              padding: 4px 10px;
              border-radius: 999px;
              background: var(--brand-soft);
              color: var(--brand);
              font-size: 10px;
              font-weight: 700;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }
            .message-card--system .message-role {
              background: #fff1cf;
              color: #8a5b08;
            }
            .message-index {
              margin-left: 8px;
              color: var(--muted);
              font-size: 11px;
            }
            .message-body {
              color: var(--ink);
            }
            .message-docs {
              margin-top: 12px;
            }
            .message-doc {
              display: inline-block;
              margin: 0 8px 8px 0;
              padding: 5px 9px;
              border-radius: 999px;
              background: rgba(20, 52, 164, 0.08);
              color: var(--brand);
              font-size: 10px;
              text-decoration: none;
            }
            .rich-text p:first-child,
            .rich-text h1:first-child,
            .rich-text h2:first-child,
            .rich-text h3:first-child,
            .rich-text h4:first-child {
              margin-top: 0;
            }
            .rich-text p:last-child {
              margin-bottom: 0;
            }
            .rich-text h1,
            .rich-text h2,
            .rich-text h3,
            .rich-text h4 {
              color: var(--ink);
              line-height: 1.25;
            }
            .rich-text a {
              color: var(--brand);
              text-decoration: none;
              word-break: break-word;
            }
            .rich-text code {
              padding: 1px 5px;
              border-radius: 6px;
              background: rgba(15, 29, 77, 0.06);
              font-size: 11px;
            }
            .rich-text pre {
              padding: 12px 14px;
              border-radius: 14px;
              background: #111827;
              color: #f9fafb;
              overflow: hidden;
              white-space: pre-wrap;
              word-break: break-word;
            }
            .rich-text blockquote {
              margin: 14px 0;
              padding: 8px 0 8px 14px;
              border-left: 4px solid rgba(20, 52, 164, 0.25);
              color: #334155;
            }
            .rich-text table {
              width: 100%;
              border-collapse: collapse;
              margin: 12px 0;
              font-size: 11px;
            }
            .rich-text th,
            .rich-text td {
              border: 1px solid #d7dfeb;
              padding: 8px 10px;
              text-align: left;
              vertical-align: top;
            }
            .rich-text th {
              background: #eef3fb;
            }
            .lexiusdoc-export {
              margin: 12px 0 0;
              padding: 14px 16px;
              border-radius: 16px;
              border: 1px solid #dbe4f3;
              background: linear-gradient(180deg, #f7faff 0%, #ffffff 100%);
            }
            .lexiusdoc-export__label {
              margin-bottom: 6px;
              color: var(--brand);
              font-size: 10px;
              font-weight: 700;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }
            .lexiusdoc-export__title {
              margin: 0 0 10px;
              font-size: 16px;
              line-height: 1.3;
            }
            .lexiusdoc-export__body > :first-child {
              margin-top: 0;
            }
            .lexiusdoc-export__body > :last-child {
              margin-bottom: 0;
            }
            .report-footer {
              margin-top: 20px;
              padding: 12px 4px 0;
              color: var(--muted);
              font-size: 10px;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="report-shell">
            <div class="report-content">
              <header class="report-header">
                <div class="report-header-top">
                  <div class="report-header-top__logo">
                    ${logoUrl ? `<img class="report-logo" src="${this.escapeHtml(logoUrl)}" alt="LEXIUS">` : ''}
                  </div>
                  <div class="report-header-top__meta">${countryName}</div>
                </div>
                <span class="report-kicker">LEXIUS AI Lawyer</span>
                <h1 class="report-title">${title}</h1>
                <p class="report-subtitle">Reporte integral de conversación listo para compartir, archivar o analizar.</p>
                <div class="meta-grid">${metadataHtml}</div>
              </header>

              <main class="body-section">
                ${noteHtml}
                ${docsHtml}
                <section class="conversation-section">
                  ${messagesHtml}
                </section>
              </main>

              <footer class="report-footer">
                Documento generado por LEXIUS ${countryName}. Los horarios se muestran en ${this.escapeHtml(timezoneInfo.displayLabel)}.
              </footer>
            </div>
          </div>
        </body>
        </html>
      `;
    },

    async collectSessionForExport(sessionKey) {
      const listSession = this.allSessions[sessionKey] || {};
      const activeSession = (
        sessionKey === this.activeSession
        && this.$refs.chatLayout
        && this.$refs.chatLayout.currentSession
        && this.$refs.chatLayout.currentSession.uuid === sessionKey
      ) ? this.$refs.chatLayout.currentSession : null;
      const localSession = activeSession || (
        listSession.is_call_log && Array.isArray(listSession.data) && listSession.data.length > 0
          ? listSession
          : this.getStoredSession(sessionKey)
      );

      if (window.sync_enabled === true) {
        try {
          const response = await fetch(
            `/chats/get?uuid=${encodeURIComponent(sessionKey)}`
            + `&artifact_envelope=${CHAT_ARTIFACT_ENVELOPE_VERSION}`,
          );
          if (response.status === 401) {
            try { handleSessionExpired("el Abogado Virtual"); } catch (e) { }
            throw new Error('session_expired');
          }
          if (!response.ok) {
            throw new Error(`fetch_failed_${response.status}`);
          }
          const payload = await response.json();
          const title = (
            (typeof payload.first_message === 'string' && payload.first_message.trim())
            || (typeof listSession.title === 'string' && listSession.title.trim())
            || (typeof localSession?.title === 'string' && localSession.title.trim())
            || 'Chat'
          );
          return {
            session: {
              ...localSession,
              ...listSession,
              uuid: sessionKey,
              title,
              path: payload.path ?? localSession?.path ?? listSession.path ?? null,
              page_title: payload.page_title ?? localSession?.page_title ?? listSession.page_title ?? null,
              is_user_document: payload.is_user_document ?? localSession?.is_user_document ?? listSession.is_user_document ?? null,
              chat_documents_state: payload.chat_documents_state ?? localSession?.chat_documents_state ?? listSession.chat_documents_state ?? [],
              is_call_log: String(payload.model || '').toLowerCase() === 'voice-call' || !!listSession.is_call_log || !!localSession?.is_call_log,
              is_multi_document: payload.is_multi_document === true || !!listSession.is_multi_document || !!localSession?.is_multi_document,
              in_progress: !!payload.in_progress,
              was_cancelled: !!payload.was_cancelled,
              created_at: localSession?.created_at || listSession.created_at || null,
              updated_at: localSession?.updated_at || listSession.updated_at || null,
            },
            messages: this.normalizeConversationForExport(payload),
            usedLocalFallback: false,
          };
        } catch (error) {
          if (error && error.message === 'session_expired') {
            throw error;
          }
          if (localSession && Array.isArray(localSession.data) && localSession.data.length > 0) {
            const filteredLocalMessages = localSession.data.filter((message) => {
              if (!message) return false;
              if (message.sender === 'bot' && message.isLoading && !message.text) return false;
              return true;
            });
            return {
              session: {
                ...localSession,
                ...listSession,
                uuid: sessionKey,
                in_progress: false,
                was_cancelled: false,
              },
              messages: filteredLocalMessages,
              usedLocalFallback: true,
            };
          }
          throw error;
        }
      }

      if (localSession && Array.isArray(localSession.data) && localSession.data.length > 0) {
        return {
          session: {
            ...localSession,
            ...listSession,
            uuid: sessionKey,
            in_progress: false,
            was_cancelled: false,
          },
          messages: localSession.data.filter((message) => !(message && message.sender === 'bot' && message.isLoading && !message.text)),
          usedLocalFallback: false,
        };
      }

      throw new Error('chat_not_found');
    },

    async exportSessionAsPDF(sessionKey, { spokenOnly = false } = {}) {
      if (!sessionKey || this.exportingSessionKey) return;
      if (
        sessionKey === this.activeSession
        && this.$refs.chatLayout
        && this.$refs.chatLayout.replying
      ) {
        this.showExportToast(`
          <div class="d-flex align-items-center gap-2">
            <i class="anticon anticon-info-circle text-theme-color"></i>
            <span>Espere a que termine la respuesta actual antes de exportar el chat completo.</span>
          </div>
        `, true);
        return;
      }

      const sessionTitle = (this.allSessions[sessionKey] && this.allSessions[sessionKey].title) || 'Chat';
      this.exportingSessionKey = sessionKey;
      this.exportProgressMessage = 'Preparando la conversación...';

      try {
        const exportBundle = await this.collectSessionForExport(sessionKey);
        const logoUrl = this.getExportLogoUrl();

        let messages = Array.isArray(exportBundle.messages) ? exportBundle.messages : [];
        if (spokenOnly && exportBundle.session?.is_call_log) {
          messages = messages.filter(message => message?.sender === 'user' || message?.sender === 'bot');
        }
        if (!messages.length) {
          throw new Error('empty_conversation');
        }
        if (exportBundle.session && exportBundle.session.in_progress === true) {
          this.showExportToast(`
            <div class="d-flex align-items-center gap-2">
              <i class="anticon anticon-info-circle text-theme-color"></i>
              <span>Este chat todavía está generando una respuesta. Espere a que termine para exportar el historial completo.</span>
            </div>
          `, true);
          return;
        }

        this.exportProgressMessage = 'Diseñando el PDF...';
        await this.waitForExportUiPaint();
        const timezoneInfo = this.resolveExportTimezone();
        const hasMissingTimestamps = messages.some((message) => (
          !message
          || (
            !message.isSyntheticExportState
            && !message.timestamp
          )
        ));
        const fallbackNotice = hasMissingTimestamps
          ? 'Algunos mensajes antiguos no incluyen una marca de tiempo individual en el historial almacenado de LEXIUS, por lo que se muestran sin hora exacta.'
          : (
              exportBundle.usedLocalFallback
                ? 'Se utilizó la copia local del dispositivo porque no fue posible consultar el servidor en este momento.'
                : ''
            );

        const html = this.buildConversationExportHtml({
          session: exportBundle.session,
          messages,
          timezoneInfo,
          logoUrl,
          fallbackNotice,
        });

        this.exportProgressMessage = 'Generando el archivo PDF...';
        await this.waitForExportUiPaint();
        const response = await fetch('/convert-html-to-pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: `Conversacion LEXIUS ${window.capitalized_country || ''}`.trim(),
            html,
            full_document: true,
          }),
        });

        if (response.status === 401) {
          try { handleSessionExpired("el Abogado Virtual"); } catch (e) { }
          throw new Error('session_expired');
        }
        if (!response.ok) {
          throw new Error(`pdf_failed_${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.style.display = 'none';
        anchor.href = url;
        anchor.download = this.sanitizeFileName(sessionTitle, '.pdf');
        document.body.appendChild(anchor);
        anchor.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(anchor);

        this.showExportToast(`
          <div class="d-flex align-items-center gap-2">
            <i class="anticon anticon-check-circle text-success"></i>
            <span>El PDF completo de <strong>${this.escapeHtml(sessionTitle)}</strong> se descargó correctamente.</span>
          </div>
        `);
      } catch (error) {
        console.error('Error exporting conversation PDF:', error);
        this.showExportToast(`
          <div class="d-flex align-items-center gap-2">
            <i class="anticon anticon-close-circle text-danger"></i>
            <span>No pudimos exportar <strong>${this.escapeHtml(sessionTitle)}</strong> en PDF. Inténtelo de nuevo.</span>
          </div>
        `, true);
      } finally {
        this.exportingSessionKey = null;
        this.exportProgressMessage = '';
      }
    },

    async renameSession(sessionKey, newTitle) {
      const rawTitle = typeof newTitle === 'string' ? newTitle : '';
      const trimmed = rawTitle.trim();
      if (!trimmed) {
        alert('El nombre no puede estar vacío.');
        return;
      }
      const title = trimmed.slice(0, 200);
      try {
        const existing = (this.allSessions[sessionKey] && this.allSessions[sessionKey].title) || '';
        if (String(existing).trim() === title) {
          return;
        }
      } catch (e) { }

      this.applySessionPatch(sessionKey, { title });

      if (window.sync_enabled === true) {
        try {
          const r = await fetch('/chats/rename', {
            method: 'POST',
            headers: (function () {
              try {
                const deviceId = new DeviceUUID().get();
                return { 'Content-Type': 'application/json', 'device-uuid': deviceId };
              } catch (e) {
                return { 'Content-Type': 'application/json' };
              }
            })(),
            body: JSON.stringify({ uuid: sessionKey, title })
          });
          if (!r.ok) {
            throw new Error('rename_failed');
          }
        } catch (e) {
          alert('No se pudo sincronizar el nuevo nombre. Se guardó localmente.');
        }
      }
    },

    async togglePinSession(sessionKey, isPinned) {
      const pinned = !!isPinned;
      this.applySessionPatch(sessionKey, { is_pinned: pinned });

      if (window.sync_enabled === true) {
        try {
          const r = await fetch('/chats/pin', {
            method: 'POST',
            headers: (function () {
              try {
                const deviceId = new DeviceUUID().get();
                return { 'Content-Type': 'application/json', 'device-uuid': deviceId };
              } catch (e) {
                return { 'Content-Type': 'application/json' };
              }
            })(),
            body: JSON.stringify({ uuid: sessionKey, is_pinned: pinned })
          });
          if (!r.ok) {
            throw new Error('pin_failed');
          }
        } catch (e) {
          alert('No se pudo sincronizar el estado fijado. Se guardó localmente.');
        }
      }
    }
  },
  mounted() {
    this.refreshSyncEnabledFlag();
    this.updateSessions()
    window.addEventListener('sync-status-updated', this.handleSyncStatusUpdate);
    document.addEventListener('keydown', this.handleDemoRecordingShortcut, true);
    this.chatDemoRecordingQueueHandler = (recordingProfile = 'desktop') => (
      this.promptAndQueueDemoRecordingForActiveChat(recordingProfile)
    );
    window.queueLexiusChatDemoRecordingForActiveChat = this.chatDemoRecordingQueueHandler;
    this.$nextTick(() => {
      this.initializeDemoRecordingMode();
    });

    // Add event listener for when the audio call modal is fully shown
    const audioCallModalEl = document.getElementById('audioCallModal');
    if (audioCallModalEl) {
      audioCallModalEl.addEventListener('shown.bs.modal', () => {
        if (window.audioCallHandler && typeof window.audioCallHandler.initialize === 'function') {
          console.log("[App.js] AudioCallModal shown, calling initialize.");
          window.audioCallHandler.initialize();
        }
      });
    }
  },
  /*html*/
  template: `
      <side-bar
        ref="sideBar"
        :allSessions="allSessions"
        :activeSession="activeSession"
        :hasMore="chatsHasMore"
        :totalCount="chatsTotal"
        :sync-enabled="syncEnabledFlag"
        :is-loading="chatsLoading"
        :using-local-fallback="chatsUsingLocalFallback"
        :load-error-message="chatsLoadErrorMessage"
        :exporting-session-key="exportingSessionKey"
        :export-progress-message="exportProgressMessage"
        @sessionSelected="sessionSelected"
        @newSessionSelected="sessionSelected"
        @deleteSession="deleteSession"
        @leaveSession="leaveSession"
        @renameSession="renameSession"
        @pinSession="togglePinSession"
        @exportSession="exportSessionAsPDF"
        @startAudioCall="handleStartAudioCall"
        @searchQueryChanged="handleSearchQueryChanged"
        @loadMore="loadMoreSessions"
        @retryLoad="retryFetchingServerChats"
      />
      <chat-layout
        ref="chatLayout"
        @sessionAdded="updateSessions()"
        @callTranscriptReceived="retainCallTranscript"
        @sessionActivated="(active)=> {activeSession = active}"
        @sessionPatched="applySessionPatch"
        @collaborationRemoved="handleCollaborationRemoved"
        @startAudioCall="handleStartAudioCall"
        @openUserFiles="openUserFilesFromChatShortcut"
      />
      <div
        v-if="demoRecordingChoiceVisible"
        class="chat-file-decision demo-recording-choice"
        data-testid="demo-recording-choice-dialog"
      >
        <button
          type="button"
          class="chat-file-decision__backdrop"
          tabindex="-1"
          aria-label="Cancelar selección de grabación"
          @click="finishDemoRecordingChoice(null)"
        ></button>
        <section
          ref="demoRecordingChoiceDialog"
          class="chat-file-decision__panel dynamic-color"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demoRecordingChoiceTitle"
          aria-describedby="demoRecordingChoiceMessage"
          @keydown.esc.stop.prevent="finishDemoRecordingChoice(null)"
          @keydown.tab="trapDemoRecordingChoiceFocus"
        >
          <button
            type="button"
            class="chat-file-decision__close"
            data-demo-recording-choice-focus
            aria-label="Cerrar"
            @click="finishDemoRecordingChoice(null)"
          >
            <i class="anticon anticon-close" aria-hidden="true"></i>
          </button>
          <span class="chat-file-decision__icon" aria-hidden="true">
            <i class="anticon anticon-video-camera"></i>
          </span>
          <div class="chat-file-decision__copy">
            <p class="chat-file-decision__eyebrow">Revisión de documento disponible</p>
            <h5 id="demoRecordingChoiceTitle">¿Qué desea grabar?</h5>
            <p id="demoRecordingChoiceMessage">
              <strong>{{ demoRecordingProfileLabel(demoRecordingChoiceProfile) }}</strong><br>
              Elija si la demo mostrará el recorrido, explorará los hallazgos o recorrerá la conversación.
            </p>
          </div>
          <div class="chat-file-decision__actions demo-recording-choice__actions">
            <button
              ref="demoRecordingReviewTourAction"
              type="button"
              class="btn btn-primary chat-file-decision__primary demo-recording-choice__action"
              data-testid="record-document-review-tour"
              data-demo-recording-choice-focus
              @click="finishDemoRecordingChoice('document-review-tour')"
            >
              <i class="anticon anticon-compass" aria-hidden="true"></i>
              <span>
                <strong>Recorrido de revisión</strong>
                <small>Muestra la revisión disponible paso a paso.</small>
              </span>
            </button>
            <button
              type="button"
              class="btn chat-file-decision__secondary demo-recording-choice__action"
              data-testid="record-document-review-workspace"
              data-demo-recording-choice-focus
              @click="finishDemoRecordingChoice('document-review-workspace')"
            >
              <i class="anticon anticon-file-search" aria-hidden="true"></i>
              <span>
                <strong>Explorar la revisión</strong>
                <small>Abre los hallazgos y sus secciones sin iniciar el recorrido.</small>
              </span>
            </button>
            <button
              type="button"
              class="btn chat-file-decision__secondary demo-recording-choice__action"
              data-testid="record-chat-scroll"
              data-demo-recording-choice-focus
              @click="finishDemoRecordingChoice('chat-scroll')"
            >
              <i class="anticon anticon-message" aria-hidden="true"></i>
              <span>
                <strong>Conversación completa</strong>
                <small>Desplaza el chat hasta el final sin abrir la revisión.</small>
              </span>
            </button>
            <button
              type="button"
              class="btn chat-file-decision__secondary demo-recording-choice__action demo-recording-choice__second-query"
              data-testid="record-document-review-second-query"
              data-demo-recording-choice-focus
              :disabled="!activeChatHasSecondQueryForRecording()"
              @click="finishDemoRecordingChoice('document-review-second-query')"
            >
              <i class="anticon anticon-fast-forward" aria-hidden="true"></i>
              <span>
                <strong>Explorar revisión y priorizar segunda consulta</strong>
                <small v-if="activeChatHasSecondQueryForRecording()">Explora el primer hallazgo y pasa por el segundo sin abrir sus detalles. Acelera la primera respuesta y termina al final de la segunda.</small>
                <small v-else>Disponible cuando el chat tiene más de una consulta.</small>
              </span>
            </button>
          </div>
        </section>
      </div>
    `
}
